<template>
	<div 
		class="rich-text-editor" 
		:class="{ 
			'is-fullscreen': isFullscreen,
			'page-mode': pageMode && isFullscreen,
			'is-resizing': isEditorPageResizing
		}"
	>
		<label v-if="label" class="editor-label">{{ label }}</label>

		<!-- Главная панель инструментов CKEditor -->
		<div ref="toolbarContainer" class="editor-toolbar"></div>

		<!-- Вторая панель: Настройки абзаца + Дополнительные функции -->
		<div 
			class="editor-toolbar editor-toolbar--secondary"
		>
			<!-- Настройки абзаца -->
			<div class="toolbar-group">
				<span class="toolbar-group-label">Абзац:</span>

				<button 
					class="toolbar-btn toolbar-btn--with-label" 
					@click="() => { console.log('🔘 Клик по кнопке Абзац'); openParagraphDialog(); }"
				>
					<span>Абзац</span>
				</button>

				<!-- Межстрочный интервал -->
				<div class="toolbar-dropdown-wrapper">
					<button 
						class="toolbar-btn toolbar-btn--with-label" 
						@click="toggleLineSpacingDropdown"
						title="Межстрочный интервал"
					>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/>
						</svg>
						<span class="btn-value">{{ currentLineHeight }}</span>
					</button>
					<div v-if="showLineSpacing" class="toolbar-dropdown">
						<div class="dropdown-header">Выберите интервал</div>
						<button 
							v-for="spacing in lineSpacingOptions" 
							:key="spacing.value"
							class="dropdown-item"
							:class="{ 'is-active': currentLineHeight === spacing.value }"
							@mousedown.prevent="setLineSpacing(spacing.value)"
						>
							{{ spacing.label }}
						</button>
					</div>
				</div>
			</div>

			<div class="toolbar-divider"></div>

			<div class="toolbar-group">
				<span class="toolbar-group-label">Текст:</span>
				<div class="toolbar-dropdown-wrapper font-family-wrapper" @mousedown.capture.prevent="rememberSelectionForFormatting">
					<button 
						class="toolbar-btn toolbar-btn--dropdown toolbar-btn--selector" 
						@mousedown.capture.prevent="rememberSelectionForFormatting"
						@click="toggleFontFamilyDropdown"
						title="Выбрать шрифт из списка"
					>
						<span class="selector-value font-family-value" :style="getFontFamilyPreviewStyle(currentFontFamily)">
							{{ resolveFontFamilyLabel(currentFontFamily) }}
						</span>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
						</svg>
					</button>
					<div v-if="showFontFamily" class="toolbar-dropdown font-family-dropdown">
						<button
							v-for="option in fontFamilyOptions"
							:key="option.value"
							class="dropdown-item"
							:class="{ 'is-active': currentFontFamily === option.value }"
							:style="getFontFamilyPreviewStyle(option.value)"
							@mousedown.prevent="applyFontFamily(option.value)"
						>
							{{ option.label }}
						</button>
					</div>
				</div>
				<div class="toolbar-dropdown-wrapper font-size-wrapper" @mousedown.capture.prevent="rememberSelectionForFormatting">
					<button 
						class="toolbar-btn toolbar-btn--dropdown toolbar-btn--selector" 
						@mousedown.capture.prevent="rememberSelectionForFormatting"
						@click="toggleFontSizeDropdown"
						title="Выбрать размер из списка"
					>
						<span class="selector-value">{{ currentFontSizeLabel }} пт</span>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
						</svg>
					</button>
					<div v-if="showFontSize" class="toolbar-dropdown font-size-dropdown">
						<button
							v-for="option in fontSizeOptions"
							:key="option.value"
							class="dropdown-item"
							:class="{ 'is-active': currentFontSize === normalizeFontSizeValue(option.value) }"
							@mousedown.prevent="applyFontSize(option.value)"
						>
							{{ option.label }}
						</button>
					</div>
				</div>
				<div class="toolbar-dropdown-wrapper">
					<button class="toolbar-btn toolbar-btn--with-label" @click="toggleCaseDropdown">
						<span>Регистр</span>
					</button>
					<div v-if="showCaseMenu" class="toolbar-dropdown">
						<button
							v-for="option in caseOptions"
							:key="option.value"
							class="dropdown-item"
							@click="applyCase(option.value)"
						>
							{{ option.label }}
						</button>
					</div>
				</div>
				<button class="toolbar-btn toolbar-btn--with-label" @click="clearFormatting">
					<span>Сбросить формат</span>
				</button>
			</div>

			<!-- Секции -->
			<button class="toolbar-btn" @click="collapseAllSections" title="Свернуть все секции">
				<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"/>
				</svg>
			</button>

			<button class="toolbar-btn" @click="expandAllSections" title="Развернуть все секции">
				<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/>
				</svg>
			</button>

			<!-- Оглавление -->
			<button 
				class="toolbar-btn"
				:class="{ 'is-active': showToc }"
				@click="toggleToc"
				title="Оглавление"
			>
				<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z"/>
				</svg>
			</button>

			<div class="toolbar-divider"></div>

			<!-- Полноэкранный режим -->
			<button 
				class="toolbar-btn"
				:class="{ 'is-active': isFullscreen }"
				@click="toggleFullscreen"
				title="Полноэкранный режим (F11)"
			>
				<svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/>
				</svg>
				<svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"/>
				</svg>
			</button>

			<!-- Настройки страницы (в полноэкранном режиме) -->
			<div class="toolbar-dropdown-wrapper" v-if="isFullscreen">
				<button 
					class="toolbar-btn toolbar-btn--with-label"
					@click="togglePageSettings"
					title="Настройки страницы"
				>
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/>
					</svg>
					<span class="btn-value">{{ getPageWidthLabel() }}</span>
				</button>
				<div v-if="showPageSettings" class="toolbar-dropdown page-settings-dropdown">
					<div class="dropdown-header">Режим страницы</div>
					<label class="dropdown-checkbox">
						<input type="checkbox" v-model="pageMode" />
						<span>Лист как в Word</span>
					</label>
					<div class="dropdown-divider"></div>
					<div class="dropdown-header">Ширина страницы</div>
					<button 
						v-for="width in pageWidthOptions" 
						:key="width.value"
						class="dropdown-item"
						:class="{ 'is-active': pageWidth === width.value }"
						@click="setPageWidth(width.value)"
						:disabled="!pageMode"
					>
						{{ width.label }}
					</button>
				</div>
			</div>

			<!-- Печать -->
			<button class="toolbar-btn" @click="printDocument" title="Печать (Ctrl+P)">
				<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659"/>
				</svg>
			</button>

			<!-- Статистика (развёртываемая) -->
			<div class="toolbar-dropdown-wrapper">
				<button 
					class="toolbar-btn toolbar-btn--with-label"
					@click="toggleStats"
					title="Статистика"
				>
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"/>
					</svg>
					<span class="btn-value">{{ wordCount }}</span>
				</button>
				<div v-if="showStats" class="toolbar-dropdown stats-dropdown">
					<div class="stats-item">
						<span class="stats-label">Слов:</span>
						<span class="stats-value">{{ wordCount }}</span>
					</div>
					<div class="stats-item">
						<span class="stats-label">Символов:</span>
						<span class="stats-value">{{ charCount }}</span>
					</div>
					<div class="stats-item">
						<span class="stats-label">Символов (без пробелов):</span>
						<span class="stats-value">{{ charCountNoSpaces }}</span>
					</div>
					<div class="stats-item">
						<span class="stats-label">Абзацев:</span>
						<span class="stats-value">{{ paragraphCount }}</span>
					</div>
					<div class="stats-item">
						<span class="stats-label">Изображений:</span>
						<span class="stats-value">{{ imageCount }}</span>
					</div>
					<div class="stats-item">
						<span class="stats-label">Время чтения:</span>
						<span class="stats-value">~{{ readingTime }} мин</span>
					</div>
				</div>
			</div>
		</div>

		<ParagraphDialog 
			v-if="showParagraphDialog"
			:initialSettings="currentDialogSettings"
			@close="closeParagraphDialog"
			@apply="applyParagraphSettings"
		/>

		<!-- Основной контейнер -->
		<div ref="editorMainContainer" class="editor-main-container" :style="editorMainContainerStyles">
			<!-- Панель оглавления -->
			<aside v-if="showToc" class="toc-panel">
				<div class="toc-header">
					<h4>Оглавление</h4>
					<button class="toc-close" @click="showToc = false">✕</button>
				</div>
				<nav class="toc-content">
					<div 
						v-for="(item, index) in tocItems" 
						:key="index"
						class="toc-item"
						:class="`toc-item--h${item.level}`"
						@click="scrollToHeading(item.id)"
					>
						<span class="toc-text">{{ item.text }}</span>
					</div>
					<div v-if="tocItems.length === 0" class="toc-empty">
						Нет заголовков
					</div>
				</nav>
			</aside>

			<!-- Контейнер редактора (лист) -->
			<div ref="editorPageContainer" class="editor-page-container" :style="pageContainerStyles">
				<div class="paper-container">
					<div class="editor-container">
						<div ref="editorElement" id="editor">
							<p></p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Статус-бар -->
		<div class="editor-status" :class="{ 'editor-status--resizing': isEditorPageResizing }">
			<div class="status-left">
				<span>Слов: {{ wordCount }}</span>
				<span class="status-divider">|</span>
				<span>Симв.: {{ charCount }}</span>
				<span class="status-divider">|</span>
				<span>~{{ readingTime }} мин</span>
				<span class="status-divider">|</span>
				<span class="status-size">Высота: {{ editorPageHeightLabel }}</span>
			</div>
			<div class="status-right">
				<span v-if="isFullscreen" class="status-badge">Полный экран</span>
				<span v-if="pageMode && isFullscreen" class="status-badge status-badge--page">Лист</span>
				<span class="status-sync" :class="`status-sync--${editorSyncStatus.type}`">
					{{ editorSyncStatus.label }}
				</span>
			</div>
			<button
				class="editor-status-resizer"
				type="button"
				title="Потяните вниз, чтобы изменить высоту редактора"
				aria-label="Изменить высоту редактора"
				aria-keyshortcuts="ArrowUp ArrowDown Shift+ArrowUp Shift+ArrowDown"
				@mousedown.stop.prevent="startEditorPageResize"
				@dblclick.stop.prevent="resetEditorPageHeight"
				@keydown="handleEditorResizerKeydown"
			></button>
		</div>

		<RichTextMediaDialog
			:open="showMediaDialog"
			:lesson-id="lessonId"
			@close="closeMediaDialog"
			@insert="handleMediaDialogInsert"
		/>
	</div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import { DecoupledEditor, editorPlugins, editorTranslations } from './ckeditor-editor'
import ParagraphDialog from './ParagraphDialog.vue'
import RichTextMediaDialog from './RichTextMediaDialog.vue'

const props = defineProps({
	modelValue: { type: String, default: '' },
	label: { type: String, default: '' },
	initialHeight: { type: [Number, String], default: null },
	lessonId: { type: [Number, String], default: null },
})

const emit = defineEmits(['update:modelValue'])

// === REFS ===
const toolbarContainer = ref(null)
const editorElement = ref(null)
const editorMainContainer = ref(null)
const editorPageContainer = ref(null)
const showMediaDialog = ref(false)
let editorInstance = null
let editorPageResizeCleanup = null
let isEditorHostAlive = false
let editorInitRequestId = 0
let latestEditorData = props.modelValue || ''
let latestExternalModelValue = props.modelValue || ''
let isApplyingExternalModelValue = false
let pendingDraftSaveTimer = null
let mediaInsertSelectionRange = null
const draftSaveState = ref('idle')
const draftSavedAt = ref(null)
const toolbarTooltipAttrs = ['title', 'data-cke-tooltip-text', 'data-cke-tooltip-position']
const toolbarTooltipTargetsSelector = '.ck-button, .ck-dropdown__button'
const customTooltipAttr = 'data-rte-tooltip'
const toolbarLabelTranslationMap = new Map([
	['undo', 'Отменить'],
	['redo', 'Повторить'],
	['heading', 'Заголовок'],
	['paragraph', 'Параграф'],
	['bold', 'Жирный'],
	['italic', 'Курсив'],
	['underline', 'Подчеркнутый'],
	['strikethrough', 'Зачеркнутый'],
	['code', 'Код'],
	['subscript', 'Нижний индекс'],
	['superscript', 'Верхний индекс'],
	['font family', 'Шрифт'],
	['font size', 'Размер шрифта'],
	['font color', 'Цвет текста'],
	['font background color', 'Цвет фона текста'],
	['text alignment', 'Выравнивание'],
	['align left', 'По левому краю'],
	['align center', 'По центру'],
	['align right', 'По правому краю'],
	['justify', 'По ширине'],
	['bulleted list', 'Маркированный список'],
	['numbered list', 'Нумерованный список'],
	['to-do list', 'Список задач'],
	['outdent', 'Уменьшить отступ'],
	['indent', 'Увеличить отступ'],
	['link', 'Ссылка'],
	['insert link', 'Вставить ссылку'],
	['unlink', 'Удалить ссылку'],
	['upload image', 'Загрузить изображение'],
	['insert image', 'Вставить изображение'],
	['insert table', 'Вставить таблицу'],
	['table column', 'Столбец таблицы'],
	['table row', 'Строка таблицы'],
	['merge cells', 'Объединить ячейки'],
	['split cell vertically', 'Разделить ячейку по вертикали'],
	['split cell horizontally', 'Разделить ячейку по горизонтали'],
	['table properties', 'Свойства таблицы'],
	['cell properties', 'Свойства ячейки'],
	['table cell properties', 'Свойства ячейки'],
	['media embed', 'Вставка медиа'],
	['block quote', 'Цитата'],
	['code block', 'Блок кода'],
	['horizontal line', 'Горизонтальная линия'],
	['page break', 'Разрыв страницы'],
	['special characters', 'Специальные символы'],
	['highlight', 'Подсветка'],
	['remove format', 'Очистить формат'],
	['find and replace', 'Найти и заменить'],
])
const LOCAL_VIDEO_EMBED_RE = /^\/?uploads\/videos\/[^?]+\.(mp4|webm|ogg|mov|m4v)(?:\?.*)?$/i
const parseVideoEmbedUrlWithPoster = rawUrl => {
	const value = String(rawUrl || '').trim()
	if (!value) return { src: '', poster: '' }
	try {
		const isAbsolute = /^https?:\/\//i.test(value)
		const parsed = new URL(value, isAbsolute ? undefined : 'https://local.invalid')
		const poster = parsed.searchParams.get('poster') || ''
		parsed.searchParams.delete('poster')
		const src = isAbsolute
			? `${parsed.origin}${parsed.pathname}${parsed.search}`
			: `${parsed.pathname}${parsed.search}`
		return { src, poster }
	} catch {
		return { src: value, poster: '' }
	}
}
const normalizeLegacyUploadedVideoMarkup = html => {
	if (!html || typeof DOMParser === 'undefined') return html || ''
	try {
		const parser = new DOMParser()
		const doc = parser.parseFromString(String(html), 'text/html')
		let changed = false
		doc.querySelectorAll('figure.rte-video-card').forEach(figure => {
			const video = figure.querySelector('video')
			if (!video) return
			const src = video.getAttribute('src') || ''
			if (!src) return
			const poster = video.getAttribute('poster') || ''
			const captionNode =
				figure.querySelector('.rte-video-card__caption') ||
				figure.querySelector('.rte-video-card__title')
			const captionText = (captionNode?.textContent || '').trim()
			const joiner = src.includes('?') ? '&' : '?'
			const embedUrl = poster
				? `${src}${joiner}poster=${encodeURIComponent(poster)}`
				: src
			figure.outerHTML =
				`<figure class="media"><oembed url="${embedUrl}"></oembed></figure>` +
				(captionText ? `<p class="rte-media-caption">${captionText}</p>` : '')
			changed = true
		})
		return changed ? doc.body.innerHTML : html
	} catch {
		return html
	}
}
const isToolbarTooltipTarget = element =>
	!!element &&
	typeof element.matches === 'function' &&
	element.matches(toolbarTooltipTargetsSelector)
const normalizeToolbarLabel = label =>
	String(label || '')
		.replace(/\u00A0/g, ' ')
		.replace(/…/g, '...')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase()
const localizeToolbarLabel = label => {
	const normalized = normalizeToolbarLabel(label)
	if (!normalized) return ''
	if (/[а-яё]/i.test(normalized)) return String(label).trim()

	if (toolbarLabelTranslationMap.has(normalized)) {
		return toolbarLabelTranslationMap.get(normalized)
	}

	if (normalized.includes('merge') && normalized.includes('cell')) return 'Объединить ячейки'
	if (normalized.includes('table') && normalized.includes('column')) return 'Столбец таблицы'
	if (normalized.includes('table') && normalized.includes('row')) return 'Строка таблицы'
	if (normalized.includes('table') && normalized.includes('propert')) return 'Свойства таблицы'
	if (normalized.includes('cell') && normalized.includes('propert')) return 'Свойства ячейки'
	if (normalized.includes('insert') && normalized.includes('table')) return 'Вставить таблицу'
	if (normalized.includes('find') && normalized.includes('replace')) return 'Найти и заменить'

	return String(label || '').trim()
}

const syncToolbarLabels = root => {
	if (!root) return
	const setAttrIfChanged = (element, attr, value) => {
		if (element.getAttribute(attr) === value) return
		element.setAttribute(attr, value)
	}
	const removeAttrIfPresent = (element, attr) => {
		if (!element.hasAttribute(attr)) return
		element.removeAttribute(attr)
	}

	const applyTitleToAria = element => {
		if (!isToolbarTooltipTarget(element) || !element.getAttribute) return
		const rawTitle = (
			element.getAttribute(customTooltipAttr) ||
			element.getAttribute('title') ||
			element.getAttribute('data-cke-tooltip-text') ||
			element.getAttribute('aria-label') ||
			''
		).trim()
		const title = localizeToolbarLabel(rawTitle)
		if (title) {
			setAttrIfChanged(element, 'aria-label', title)
			setAttrIfChanged(element, customTooltipAttr, title)
		} else {
			removeAttrIfPresent(element, customTooltipAttr)
		}
		toolbarTooltipAttrs.forEach(attr => removeAttrIfPresent(element, attr))
	}

	applyTitleToAria(root)
	if (typeof root.querySelectorAll === 'function') {
		root
			.querySelectorAll(toolbarTooltipTargetsSelector)
			.forEach(applyTitleToAria)
	}
}

const observeToolbarTitles = root => {
	if (!root || typeof MutationObserver === 'undefined') return null

	syncToolbarLabels(root)

	const observer = new MutationObserver(mutations => {
		let requiresFullSync = false

		mutations.forEach(mutation => {
			if (
				mutation.type === 'attributes' &&
				(toolbarTooltipAttrs.includes(mutation.attributeName) ||
					mutation.attributeName === 'aria-label')
			) {
				syncToolbarLabels(mutation.target)
				return
			}

			if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
				requiresFullSync = true
			}
		})

		if (requiresFullSync) {
			syncToolbarLabels(root)
		}
	})

	observer.observe(root, {
		subtree: true,
		childList: true,
		attributes: true,
		attributeFilter: [...toolbarTooltipAttrs, 'aria-label'],
	})

	return observer
}

// === UI STATE ===
const showLineSpacing = ref(false)
const showToc = ref(false)
const showPageSettings = ref(false)
const showStats = ref(false)
const isFullscreen = ref(false)
const pageMode = ref(true)
const pageWidth = ref('a4')
const currentLineHeight = ref('1.5')
const tocItems = ref([])
const showFontFamily = ref(false)
const showFontSize = ref(false)
const showCaseMenu = ref(false)
const showParagraphDialog = ref(false)
const currentDialogSettings = ref({})
const currentFontFamily = ref('default')
const currentFontSize = ref('default')
const manualEditorPageHeight = ref(null)
const isEditorPageResizing = ref(false)
const legacyFormattingMarkerName = 'rte-formatting-selection'
let formattingSelectionRange = null

// === OPTIONS ===
const lineSpacingOptions = [
	{ value: '1', label: 'Одинарный (1.0)' },
	{ value: '1.15', label: '1.15' },
	{ value: '1.5', label: 'Полуторный (1.5)' },
	{ value: '2', label: 'Двойной (2.0)' },
	{ value: '2.5', label: '2.5' },
	{ value: '3', label: 'Тройной (3.0)' },
]

const fontFamilyOptions = [
	{ value: 'default', label: 'По умолчанию' },
	{ value: 'Calibri, sans-serif', label: 'Calibri' },
	{ value: 'Calibri Light, sans-serif', label: 'Calibri Light' },
	{ value: 'Cambria, serif', label: 'Cambria' },
	{ value: 'Candara, sans-serif', label: 'Candara' },
	{ value: 'Constantia, serif', label: 'Constantia' },
	{ value: 'Corbel, sans-serif', label: 'Corbel' },
	{ value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
	{ value: 'Arial Black, Gadget, sans-serif', label: 'Arial Black' },
	{ value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
	{ value: 'Franklin Gothic Medium, Arial Narrow, sans-serif', label: 'Franklin Gothic Medium' },
	{ value: 'Century Gothic, CenturyGothic, AppleGothic, sans-serif', label: 'Century Gothic' },
	{ value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
	{ value: 'Trebuchet MS, Helvetica, sans-serif', label: 'Trebuchet MS' },
	{ value: 'Segoe UI, Tahoma, Geneva, sans-serif', label: 'Segoe UI' },
	{ value: 'Gill Sans, Gill Sans MT, Calibri, sans-serif', label: 'Gill Sans' },
	{ value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
	{ value: 'Lucida Sans Unicode, Lucida Grande, sans-serif', label: 'Lucida Sans' },
	{ value: 'Times New Roman, Times, serif', label: 'Times New Roman' },
	{ value: 'Georgia, serif', label: 'Georgia' },
	{ value: 'Garamond, serif', label: 'Garamond' },
	{ value: 'Palatino Linotype, Book Antiqua, Palatino, serif', label: 'Palatino Linotype' },
	{ value: 'Book Antiqua, Palatino, serif', label: 'Book Antiqua' },
	{ value: 'Bookman, URW Bookman L, serif', label: 'Bookman' },
	{ value: 'Courier New, Courier, monospace', label: 'Courier New' },
	{ value: 'Consolas, Monaco, monospace', label: 'Consolas' },
	{ value: 'Roboto, Arial, sans-serif', label: 'Roboto' },
	{ value: 'Open Sans, Arial, sans-serif', label: 'Open Sans' },
	{ value: 'PT Sans, Arial, sans-serif', label: 'PT Sans' },
	{ value: 'Comic Sans MS, Comic Sans, cursive', label: 'Comic Sans MS' },
	{ value: 'Impact, Charcoal, sans-serif', label: 'Impact' },
]

const fontSizeOptions = [
	{ value: '8pt', label: '8' },
	{ value: '9pt', label: '9' },
	{ value: '10pt', label: '10' },
	{ value: '11pt', label: '11' },
	{ value: '12pt', label: '12' },
	{ value: '14pt', label: '14' },
	{ value: '16pt', label: '16' },
	{ value: '18pt', label: '18' },
	{ value: '20pt', label: '20' },
	{ value: '22pt', label: '22' },
	{ value: '24pt', label: '24' },
	{ value: '26pt', label: '26' },
	{ value: '28pt', label: '28' },
	{ value: '36pt', label: '36' },
	{ value: '48pt', label: '48' },
	{ value: '72pt', label: '72' },
]

const caseOptions = [
	{ value: 'lower', label: 'нижний регистр' },
	{ value: 'upper', label: 'ВЕРХНИЙ РЕГИСТР' },
	{ value: 'sentence', label: 'Как в предложении' },
	{ value: 'title', label: 'Каждое Слово С Заглавной' },
]

const pageWidthOptions = [
	{ value: 'narrow', label: 'Узкий (600px)' },
	{ value: 'a4', label: 'A4 (816px)' },
	{ value: 'wide', label: 'Широкий (1000px)' },
	{ value: 'full', label: 'На всю ширину' },
]

// === COMPUTED ===

// Декодирование HTML entities
const decodeHtmlEntities = (html) => {
	const entities = {
		'&nbsp;': ' ', '&lt;': '<', '&gt;': '>', '&amp;': '&',
		'&quot;': '"', '&apos;': "'", '&#39;': "'",
		'&mdash;': '—', '&ndash;': '–', '&hellip;': '…',
		'&laquo;': '«', '&raquo;': '»', '&copy;': '©',
		'&reg;': '®', '&trade;': '™', '&euro;': '€',
		'&pound;': '£', '&yen;': '¥', '&cent;': '¢'
	}
	let result = html
	for (const [entity, char] of Object.entries(entities)) {
		result = result.replace(new RegExp(entity, 'gi'), char)
	}
	// Числовые entities (&#123; или &#x7B;)
	result = result.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
	result = result.replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)))
	return result
}

const textWithoutTags = computed(() => {
	if (!props.modelValue) return ''
	const text = decodeHtmlEntities(props.modelValue)
		.replace(/<br\s*\/?>/gi, ' ')           // <br> -> пробел
		.replace(/<\/?(p|div|li|h[1-6])[^>]*>/gi, ' ') // блочные теги -> пробел
		.replace(/<[^>]*>/g, '')                // остальные теги удаляем
		.replace(/\s+/g, ' ')                   // множественные пробелы
		.trim()
	return text
})

const wordCount = computed(() => {
	const text = textWithoutTags.value
	if (!text) return 0
	// Учитываем кириллицу, латиницу, числа
	const words = text.match(/[\p{L}\p{N}]+/gu)
	return words ? words.length : 0
})

const charCount = computed(() => textWithoutTags.value.length)

const charCountNoSpaces = computed(() => 
	textWithoutTags.value.replace(/\s/g, '').length
)

const paragraphCount = computed(() => {
	if (!props.modelValue) return 0
	const html = props.modelValue
	
	// Считаем блочные элементы с текстовым контентом
	// CKEditor использует: <p>, <h1>-<h6>, <li>, <blockquote>
	const blockTags = html.match(/<(p|h[1-6]|li|blockquote)[^>]*>[\s\S]*?<\/\1>/gi) || []
	
	// Фильтруем только блоки с реальным текстом
	const nonEmpty = blockTags.filter(block => {
		const content = block
			.replace(/<[^>]*>/g, '')      // убираем теги
			.replace(/&nbsp;/g, '')       // убираем неразрывные пробелы
			.replace(/\s+/g, '')          // убираем пробелы
			.trim()
		return content.length > 0
	})
	
	return nonEmpty.length
})

const imageCount = computed(() => {
	if (!props.modelValue) return 0
	const html = props.modelValue
	
	const imgTags = html.match(/<img\s[^>]*>/gi) || []
	
	// Фильтруем только реальные изображения контента (не иконки)
	const contentImages = imgTags.filter(img => {
		// Исключаем маленькие иконки (width/height <= 24)
		const widthMatch = img.match(/width=["']?(\d+)/i)
		const heightMatch = img.match(/height=["']?(\d+)/i)
		if (widthMatch && parseInt(widthMatch[1]) <= 24) return false
		if (heightMatch && parseInt(heightMatch[1]) <= 24) return false
		
		// Исключаем иконки по классам
		if (/class=["'][^"']*icon/i.test(img)) return false
		if (/class=["'][^"']*ck-/i.test(img)) return false
		
		// Исключаем data-uri иконки (обычно маленькие base64)
		const srcMatch = img.match(/src=["']([^"']+)/i)
		if (srcMatch) {
			const src = srcMatch[1]
			if (src.startsWith('data:') && src.length < 500) return false
		}
		
		return true
	})
	
	return contentImages.length
})

const readingTime = computed(() => {
	const wordsPerMinute = 200 // средняя скорость чтения
	const words = wordCount.value
	if (words === 0) return 0
	
	const minutes = words / wordsPerMinute
	
	// Для коротких текстов показываем "< 1"
	if (minutes < 1) return '< 1'
	
	// Округляем до целых минут
	return Math.ceil(minutes)
})

const pageContainerStyles = computed(() => {
	const styles = {}
	if (isFullscreen.value && pageMode.value) {
		const widths = { narrow: '600px', a4: '816px', wide: '1000px', full: '100%' }
		styles.maxWidth = widths[pageWidth.value] || '816px'
	}
	return styles
})

const currentFontSizeLabel = computed(() =>
	currentFontSize.value === 'default' ? '12' : currentFontSize.value
)

const getDraftStorageKey = () => {
	const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown-path'
	const labelPart = props.label ? String(props.label).trim() : 'no-label'
	return `rte-draft:${path}:${labelPart}`
}

const clearPendingDraftSave = () => {
	if (!pendingDraftSaveTimer) return
	clearTimeout(pendingDraftSaveTimer)
	pendingDraftSaveTimer = null
}

const saveDraftSnapshot = data => {
	try {
		const key = getDraftStorageKey()
		const payload = JSON.stringify({ data, ts: Date.now() })
		localStorage.setItem(key, payload)
		draftSaveState.value = 'saved'
		draftSavedAt.value = Date.now()
		return true
	} catch (error) {
		draftSaveState.value = 'error'
		console.warn('[RichTextEditor] Failed to save local draft snapshot:', error)
		return false
	}
}

const scheduleDraftSave = data => {
	clearPendingDraftSave()
	draftSaveState.value = 'saving'
	pendingDraftSaveTimer = setTimeout(() => {
		saveDraftSnapshot(data)
		pendingDraftSaveTimer = null
	}, 600)
}

const readDraftSnapshot = () => {
	try {
		const raw = localStorage.getItem(getDraftStorageKey())
		if (!raw) return null
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed.data !== 'string') return null
		return parsed
	} catch (error) {
		console.warn('[RichTextEditor] Failed to read local draft snapshot:', error)
		return null
	}
}

const clearDraftSnapshot = () => {
	try {
		localStorage.removeItem(getDraftStorageKey())
		draftSaveState.value = 'idle'
	} catch (error) {
		draftSaveState.value = 'error'
		console.warn('[RichTextEditor] Failed to clear local draft snapshot:', error)
	}
}

const hasPotentialUnsavedChanges = () =>
	Boolean(latestEditorData && latestEditorData !== (latestExternalModelValue || ''))

const formatTimeForStatus = ts => {
	if (!ts) return ''
	try {
		const date = new Date(ts)
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
	} catch {
		return ''
	}
}

const editorSyncStatus = computed(() => {
	if (draftSaveState.value === 'error') {
		return { type: 'error', label: 'Ошибка сохранения' }
	}
	if (hasPotentialUnsavedChanges()) {
		if (draftSaveState.value === 'saving') {
			return { type: 'saving', label: 'Сохраняю черновик...' }
		}
		return { type: 'dirty', label: 'Есть несохраненные изменения' }
	}
	if (draftSaveState.value === 'saved' && draftSavedAt.value) {
		return { type: 'saved', label: `Черновик сохранен ${formatTimeForStatus(draftSavedAt.value)}` }
	}
	return { type: 'idle', label: 'Все изменения сохранены' }
})

const handleBeforeUnload = event => {
	if (!hasPotentialUnsavedChanges()) return
	event.preventDefault()
	event.returnValue = ''
}

const editorMainContainerStyles = computed(() => {
	if (isFullscreen.value) return {}
	if (!manualEditorPageHeight.value) return {}
	return {
		flex: '0 0 auto',
		height: `${manualEditorPageHeight.value}px`,
		minHeight: `${manualEditorPageHeight.value}px`,
	}
})

const editorPageHeightLabel = computed(() => {
	const container = editorMainContainer.value || editorPageContainer.value
	const currentHeight = manualEditorPageHeight.value || container?.getBoundingClientRect().height
	if (!currentHeight) return 'авто'
	return `${Math.round(currentHeight)} px`
})

const clampEditorPageHeight = value => {
	const minHeight = 320
	const maxHeight = 2600
	return Math.min(maxHeight, Math.max(minHeight, Math.round(value)))
}

const getDefaultEditorPageHeight = () => {
	const numericHeight = Number(props.initialHeight)
	if (!Number.isFinite(numericHeight) || numericHeight <= 0) return null
	return clampEditorPageHeight(numericHeight)
}

const stopEditorPageResize = () => {
	isEditorPageResizing.value = false
	if (!editorPageResizeCleanup) return
	editorPageResizeCleanup()
	editorPageResizeCleanup = null
}

const resetEditorPageHeight = () => {
	manualEditorPageHeight.value = getDefaultEditorPageHeight()
	isEditorPageResizing.value = false
}

const nudgeEditorPageHeight = delta => {
	const container = editorMainContainer.value || editorPageContainer.value
	const baseHeight = manualEditorPageHeight.value || container?.getBoundingClientRect().height || 600
	manualEditorPageHeight.value = clampEditorPageHeight(baseHeight + delta)
}

const handleEditorResizerKeydown = event => {
	const key = event.key
	if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'Home') return
	event.preventDefault()
	if (key === 'Home') {
		resetEditorPageHeight()
		return
	}
	const step = event.shiftKey ? 80 : 24
	nudgeEditorPageHeight(key === 'ArrowDown' ? step : -step)
}

const startEditorPageResize = event => {
	if (event.button !== 0) return
	event.stopPropagation()
	const container = editorMainContainer.value || editorPageContainer.value
	if (!container) return

	stopEditorPageResize()
	const activeElement = document.activeElement
	if (
		activeElement &&
		container.contains(activeElement) &&
		typeof activeElement.blur === 'function'
	) {
		activeElement.blur()
	}

	const activationThresholdPx = 2
	const startY = event.clientY
	const startHeight = Math.round(container.getBoundingClientRect().height)
	const maxHeightForSession = Math.max(2600, startHeight)
	const clampHeightForSession = value => {
		const minHeight = 320
		return Math.min(maxHeightForSession, Math.max(minHeight, Math.round(value)))
	}
	const body = document.body
	const prevUserSelect = body.style.userSelect
	const prevCursor = body.style.cursor
	let isResizeActivated = false
	let lastClientY = startY
	let currentHeight = startHeight

	const activateResizeMode = () => {
		if (isResizeActivated) return
		isResizeActivated = true
		isEditorPageResizing.value = true
		body.style.userSelect = 'none'
		body.style.cursor = 'ns-resize'
		currentHeight = startHeight
		manualEditorPageHeight.value = currentHeight
	}

	const onMouseUp = () => {
		window.removeEventListener('mousemove', onMouseMove)
		window.removeEventListener('mouseup', onMouseUp)
		window.removeEventListener('blur', onWindowBlur)
		document.removeEventListener('visibilitychange', onVisibilityChange)
		body.style.userSelect = prevUserSelect
		body.style.cursor = prevCursor
		isEditorPageResizing.value = false
		editorPageResizeCleanup = null
	}

	const onWindowBlur = () => {
		onMouseUp()
	}

	const onVisibilityChange = () => {
		if (document.visibilityState === 'hidden') {
			onMouseUp()
		}
	}

	const onMouseMove = moveEvent => {
		if (
			typeof moveEvent.buttons === 'number' &&
			(moveEvent.buttons & 1) !== 1
		) {
			onMouseUp()
			return
		}
		const totalDelta = moveEvent.clientY - startY
		if (!isResizeActivated) {
			if (Math.abs(totalDelta) < activationThresholdPx) return
			activateResizeMode()
			currentHeight = clampHeightForSession(startHeight + totalDelta)
			manualEditorPageHeight.value = currentHeight
			lastClientY = moveEvent.clientY
			return
		}
		const deltaFromLast = moveEvent.clientY - lastClientY
		if (deltaFromLast === 0) return
		currentHeight = clampHeightForSession(currentHeight + deltaFromLast)
		manualEditorPageHeight.value = currentHeight
		lastClientY = moveEvent.clientY
	}

	window.addEventListener('mousemove', onMouseMove)
	window.addEventListener('mouseup', onMouseUp)
	window.addEventListener('blur', onWindowBlur)
	document.addEventListener('visibilitychange', onVisibilityChange)
	editorPageResizeCleanup = onMouseUp
}

// === LIFECYCLE ===
onMounted(async () => {
	isEditorHostAlive = true
	latestExternalModelValue = props.modelValue || ''
	latestEditorData = props.modelValue || ''
	manualEditorPageHeight.value = getDefaultEditorPageHeight()
	try {
		await nextTick()
		await initEditor()
	} catch (error) {
		console.error('[RichTextEditor] CKEditor failed to load/init.', error)
	}
	document.addEventListener('keydown', handleGlobalKeydown)
	document.addEventListener('click', handleGlobalClick)
	window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
	isEditorHostAlive = false
	editorInitRequestId += 1
	showMediaDialog.value = false
	clearMediaInsertSelectionTarget()
	stopEditorPageResize()
	if (hasPotentialUnsavedChanges()) {
		saveDraftSnapshot(latestEditorData)
	}
	clearPendingDraftSave()
	if (editorInstance) {
		if (editorInstance._scrollContainer && editorInstance._scrollSyncHandler) {
			editorInstance._scrollContainer.removeEventListener('scroll', editorInstance._scrollSyncHandler)
		}
		if (editorInstance._toolbarDomElement && editorInstance._toolbarMouseDownHandler) {
			editorInstance._toolbarDomElement.removeEventListener('mousedown', editorInstance._toolbarMouseDownHandler, true)
		}
		if (editorInstance._toolbarDomElement && editorInstance._toolbarTouchStartHandler) {
			editorInstance._toolbarDomElement.removeEventListener('touchstart', editorInstance._toolbarTouchStartHandler, true)
		}
		if (editorInstance._toolbarDomElement && editorInstance._toolbarClickHandler) {
			editorInstance._toolbarDomElement.removeEventListener('click', editorInstance._toolbarClickHandler, true)
		}
		if (editorInstance._badgeObserver) editorInstance._badgeObserver.disconnect()
		if (editorInstance._toolbarTitleObserver) editorInstance._toolbarTitleObserver.disconnect()
		if (editorInstance._bodyWrapperTooltipObserver) editorInstance._bodyWrapperTooltipObserver.disconnect()
		if (editorInstance._bodyWrapperBootstrapObserver) editorInstance._bodyWrapperBootstrapObserver.disconnect()
		Promise.resolve(editorInstance.destroy()).catch(error => {
			console.warn('[RichTextEditor] Error during editor destroy:', error)
		})
	}
	document.removeEventListener('keydown', handleGlobalKeydown)
	document.removeEventListener('click', handleGlobalClick)
	window.removeEventListener('beforeunload', handleBeforeUnload)
})

const isMediaEmbedToolbarButton = target => {
	if (!target || !target.matches) return false
	const ariaLabel = target.getAttribute('aria-label') || ''
	const tooltipLabel = target.getAttribute(customTooltipAttr) || ''
	const visibleLabel = target.textContent || ''
	const normalizedLabel = `${ariaLabel} ${tooltipLabel} ${visibleLabel}`.toLowerCase()
	return normalizedLabel.includes('медиа') || normalizedLabel.includes('media')
}

const captureMediaInsertSelectionTarget = () => {
	if (!editorInstance) return false
	return runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		const firstRange = selection?.getFirstRange?.()
		if (!firstRange) return false
		mediaInsertSelectionRange = firstRange.clone()
		return true
	}, false)
}

const restoreMediaInsertSelectionTarget = () => {
	if (!editorInstance || !mediaInsertSelectionRange) return false
	return runWithSafeSelectionAccess(() => {
		editorInstance.model.change(writer => {
			writer.setSelection(mediaInsertSelectionRange)
		})
		return true
	}, false)
}

const clearMediaInsertSelectionTarget = () => {
	mediaInsertSelectionRange = null
}

const openMediaDialog = () => {
	captureMediaInsertSelectionTarget()
	showMediaDialog.value = true
}

const closeMediaDialog = () => {
	showMediaDialog.value = false
	clearMediaInsertSelectionTarget()
}

const insertHtmlIntoEditor = html => {
	if (!editorInstance || !html) return
	restoreMediaInsertSelectionTarget()
	editorInstance.model.change(() => {
		const viewFragment = editorInstance.data.processor.toView(html)
		const modelFragment = editorInstance.data.toModel(viewFragment)
		editorInstance.model.insertContent(modelFragment)
	})
	clearMediaInsertSelectionTarget()
	nextTick(() => {
		editorInstance?.editing?.view?.focus?.()
	})
}

const handleMediaDialogInsert = html => {
	insertHtmlIntoEditor(html)
	closeMediaDialog()
}

// === CKEDITOR ===
async function initEditor() {
	try {
		const editableHost = editorElement.value
		const toolbarHost = toolbarContainer.value
		if (!editableHost || !toolbarHost) {
			console.warn('[RichTextEditor] Пропуск инициализации CKEditor: отсутствуют DOM-контейнеры.')
			return
		}
		const requestId = ++editorInitRequestId

		const editor = await DecoupledEditor.create(editableHost, {
			licenseKey: 'GPL',
			language: 'ru',
			translations: editorTranslations,
			plugins: editorPlugins,
			toolbar: {
				items: [
					'undo', 'redo', '|',
					'heading', '|',
					'bold', 'italic', 'underline', 'strikethrough', 'code', 'subscript', 'superscript', '|',
					'fontColor', 'fontBackgroundColor', '|',
					'alignment', '|',
					'bulletedList', 'numberedList', 'todoList', '|',
					'outdent', 'indent', '|',
					'link', 'uploadImage', 'insertTable', 'tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties', 'mediaEmbed', 'blockQuote', 'codeBlock', '|',
					'horizontalLine', 'pageBreak', 'specialCharacters', '|',
					'highlight', 'removeFormat', '|',
					'findAndReplace'
				],
				shouldNotGroupWhenFull: true
			},
			heading: {
				options: [
					{ model: 'paragraph', title: 'Параграф', class: 'ck-heading_paragraph' },
					{ model: 'heading1', view: 'h1', title: 'Заголовок 1', class: 'ck-heading_heading1' },
					{ model: 'heading2', view: 'h2', title: 'Заголовок 2', class: 'ck-heading_heading2' },
					{ model: 'heading3', view: 'h3', title: 'Заголовок 3', class: 'ck-heading_heading3' },
					{ model: 'heading4', view: 'h4', title: 'Заголовок 4', class: 'ck-heading_heading4' }
				]
			},
			fontSize: { 
				options: [
					{ title: '8', model: '8pt' },
					{ title: '9', model: '9pt' },
					{ title: '10', model: '10pt' },
					{ title: '11', model: '11pt' },
					{ title: '12', model: '12pt' },
					{ title: '14', model: '14pt' },
					{ title: '16', model: '16pt' },
					{ title: '18', model: '18pt' },
					{ title: '20', model: '20pt' },
					{ title: '22', model: '22pt' },
					{ title: '24', model: '24pt' },
					{ title: '26', model: '26pt' },
					{ title: '28', model: '28pt' },
					{ title: '36', model: '36pt' },
					{ title: '48', model: '48pt' },
					{ title: '72', model: '72pt' }
				], 
				supportAllValues: true 
			},
			fontFamily: {
				options: [
					'default',
					'Calibri, sans-serif',
					'Calibri Light, sans-serif',
					'Cambria, serif',
					'Candara, sans-serif',
					'Constantia, serif',
					'Corbel, sans-serif',
					'Arial, Helvetica, sans-serif',
					'Arial Black, Gadget, sans-serif',
					'Helvetica, Arial, sans-serif',
					'Franklin Gothic Medium, Arial Narrow, sans-serif',
					'Century Gothic, CenturyGothic, AppleGothic, sans-serif',
					'Tahoma, Geneva, sans-serif',
					'Trebuchet MS, Helvetica, sans-serif',
					'Segoe UI, Tahoma, Geneva, sans-serif',
					'Gill Sans, Gill Sans MT, Calibri, sans-serif',
					'Verdana, Geneva, sans-serif',
					'Lucida Sans Unicode, Lucida Grande, sans-serif',
					'Times New Roman, Times, serif',
					'Georgia, serif',
					'Garamond, serif',
					'Palatino Linotype, Book Antiqua, Palatino, serif',
					'Book Antiqua, Palatino, serif',
					'Bookman, URW Bookman L, serif',
					'Courier New, Courier, monospace',
					'Consolas, Monaco, monospace',
					'Roboto, Arial, sans-serif',
					'Open Sans, Arial, sans-serif',
					'PT Sans, Arial, sans-serif',
					'Comic Sans MS, Comic Sans, cursive',
					'Impact, Charcoal, sans-serif'
				],
				supportAllValues: true
			},
			image: {
				resizeUnit: '%',
				resizeOptions: [
					{ name: 'resizeImage:original', value: null, label: 'Оригинал' },
					{ name: 'resizeImage:25', value: '25', label: '25%' },
					{ name: 'resizeImage:50', value: '50', label: '50%' },
					{ name: 'resizeImage:75', value: '75', label: '75%' }
				],
				toolbar: [
					'imageStyle:inline', 
					'imageStyle:block', 
					'imageStyle:side', 
					'|', 
					'resizeImage',
					'|',
					'toggleImageCaption', 
					'imageTextAlternative', 
					'|', 
					'linkImage'
				]
			},
			table: {
				contentToolbar: [
					'tableColumn', 'tableRow', 'mergeTableCells', '|',
					'tableProperties', 'tableCellProperties'
				]
			},
			mediaEmbed: {
				toolbar: ['mediaEmbed'],
				extraProviders: [
					{
						name: 'localUploadVideo',
						url: LOCAL_VIDEO_EMBED_RE,
						html: match => {
							const matchedUrl = Array.isArray(match) ? match[0] : ''
							const { src, poster } = parseVideoEmbedUrlWithPoster(matchedUrl)
							if (!src) return ''
							return `
								<div class="ck-media__wrapper" data-oembed-url="${matchedUrl}">
									<video
										src="${src}"
										${poster ? `poster="${poster}"` : ''}
										controls
										preload="metadata"
										playsinline
										style="display:block;width:100%;height:100%;background:#020617;"
									></video>
								</div>
							`
						},
					},
				],
			},
			link: {
				decorators: {
					openInNewTab: { mode: 'manual', label: 'Открыть в новой вкладке', attributes: { target: '_blank', rel: 'noopener noreferrer' } }
				}
			},
			htmlSupport: { allow: [{ name: /.*/, attributes: true, classes: true, styles: true }] }
		})
		
		const activeToolbarHost = toolbarContainer.value
		if (
			!isEditorHostAlive ||
			requestId !== editorInitRequestId ||
			!activeToolbarHost ||
			!activeToolbarHost.isConnected
		) {
			await editor.destroy()
			return
		}

		const ckToolbarElement = editor.ui.view.toolbar.element
		activeToolbarHost.appendChild(ckToolbarElement)
		const toolbarTitleObserver = observeToolbarTitles(ckToolbarElement)
		let bodyWrapperTooltipObserver = null
		let bodyWrapperBootstrapObserver = null
		const attachBodyWrapperTooltipObserver = () => {
			const bodyWrapper = document.querySelector('.ck-body-wrapper')
			if (!bodyWrapper) return false
			bodyWrapperTooltipObserver = observeToolbarTitles(bodyWrapper)
			return true
		}
		if (!attachBodyWrapperTooltipObserver() && typeof MutationObserver !== 'undefined') {
			bodyWrapperBootstrapObserver = new MutationObserver(() => {
				if (!attachBodyWrapperTooltipObserver()) return
				bodyWrapperBootstrapObserver?.disconnect()
				bodyWrapperBootstrapObserver = null
				if (editorInstance) {
					editorInstance._bodyWrapperTooltipObserver = bodyWrapperTooltipObserver
					editorInstance._bodyWrapperBootstrapObserver = null
				}
			})
			bodyWrapperBootstrapObserver.observe(document.body, {
				subtree: true,
				childList: true,
			})
		}
		let suppressNativeBoldUntil = 0
		let suppressNativeMediaUntil = 0
		const isBoldToolbarButton = target => {
			if (!target || !target.matches) return false
			if (target.matches('.ck-button.ck-bold')) return true
			const ariaLabel = target.getAttribute('aria-label') || ''
			return ariaLabel.includes('Жир') || ariaLabel.includes('Bold')
		}
		const toolbarSelectionCaptureHandler = event => {
			const target = event.target?.closest?.('.ck-button, .ck-dropdown__button')
			if (!target) return
			if (isMediaEmbedToolbarButton(target)) {
				captureMediaInsertSelectionTarget()
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation?.()
				openMediaDialog()
				suppressNativeMediaUntil = Date.now() + 350
				return
			}
			if (!isBoldToolbarButton(target)) return

			captureFormattingSelectionTarget()
			event.preventDefault()
			event.stopPropagation()
			event.stopImmediatePropagation?.()
			toggleBoldOnFormattingSelection()
			suppressNativeBoldUntil = Date.now() + 350
		}
		const toolbarClickCaptureHandler = event => {
			const target = event.target?.closest?.('.ck-button, .ck-dropdown__button')
			if (target && isMediaEmbedToolbarButton(target)) {
				if (Date.now() <= suppressNativeMediaUntil) {
					event.preventDefault()
					event.stopPropagation()
					event.stopImmediatePropagation?.()
				}
				return
			}
			if (!target || !isBoldToolbarButton(target)) return
			if (Date.now() > suppressNativeBoldUntil) return
			event.preventDefault()
			event.stopPropagation()
			event.stopImmediatePropagation?.()
		}
		ckToolbarElement.addEventListener('mousedown', toolbarSelectionCaptureHandler, true)
		ckToolbarElement.addEventListener('touchstart', toolbarSelectionCaptureHandler, true)
		ckToolbarElement.addEventListener('click', toolbarClickCaptureHandler, true)

		// Удаляем старый marker из предыдущей реализации (если остался после HMR).
		if (editor.model.markers.has(legacyFormattingMarkerName)) {
			editor.model.change(writer => {
				writer.removeMarker(legacyFormattingMarkerName)
			})
		}
		
		const initialModelData = props.modelValue || ''
		const draftSnapshot = readDraftSnapshot()
		const shouldRestoreDraft = !initialModelData && draftSnapshot?.data
		const initialDataToLoad = normalizeLegacyUploadedVideoMarkup(
			shouldRestoreDraft ? draftSnapshot.data : initialModelData
		)

		if (initialDataToLoad) {
			try {
				isApplyingExternalModelValue = true
				editor.setData(initialDataToLoad)
				latestEditorData = initialDataToLoad
			} catch (error) {
				console.error('[RichTextEditor] Failed to set initial editor data:', error)
			} finally {
				isApplyingExternalModelValue = false
			}
		}
		if (shouldRestoreDraft && initialDataToLoad) {
			emit('update:modelValue', initialDataToLoad)
		}
		
		editor.model.document.on('change:data', () => {
			try {
				const data = editor.getData()
				latestEditorData = data
				if (data !== (latestExternalModelValue || '')) {
					draftSaveState.value = 'dirty'
				}
				scheduleDraftSave(data)

				if (!isApplyingExternalModelValue) {
					emit('update:modelValue', data)
				}
				nextTick(() => updateToc())
			} catch (error) {
				console.error('[RichTextEditor] Failed while processing change:data:', error)
			}
		})
		
		editorInstance = editor
		window.editorInstance = editor
		editorInstance._toolbarTitleObserver = toolbarTitleObserver
		editorInstance._bodyWrapperTooltipObserver = bodyWrapperTooltipObserver
		editorInstance._bodyWrapperBootstrapObserver = bodyWrapperBootstrapObserver
		editorInstance._toolbarDomElement = ckToolbarElement
		editorInstance._toolbarMouseDownHandler = toolbarSelectionCaptureHandler
		editorInstance._toolbarTouchStartHandler = toolbarSelectionCaptureHandler
		editorInstance._toolbarClickHandler = toolbarClickCaptureHandler

		const commandsWithSelectionRestore = new Set([
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'subscript',
			'superscript',
			'fontFamily',
			'fontSize',
			'fontColor',
			'fontBackgroundColor',
			'removeFormat',
		])
		const originalExecute = editor.execute.bind(editor)
		editor.execute = (commandName, ...args) => {
			if (commandName === 'bold' && Date.now() <= suppressNativeBoldUntil) {
				return
			}
			if (commandName === 'mediaEmbed') {
				openMediaDialog()
				suppressNativeMediaUntil = Date.now() + 350
				return
			}
			if (commandsWithSelectionRestore.has(commandName)) {
				const shouldRestoreSelection = runWithSafeSelectionAccess(() => {
					const selection = editor.model.document.selection
					return !!selection?.isCollapsed && !!formattingSelectionRange
				}, false)
				if (shouldRestoreSelection) {
					try {
						editor.model.change(writer => {
							writer.setSelection(formattingSelectionRange)
						})
					} catch (error) {
						console.warn('[RichTextEditor] Не удалось восстановить выделение перед командой:', error)
						formattingSelectionRange = null
					}
				}
			}
			return originalExecute(commandName, ...args)
		}
		
		// Конвертер для htmlAttributes.styles -> style в DOM
		editor.conversion.for('downcast').add(dispatcher => {
			dispatcher.on('attribute:htmlAttributes', (evt, data, conversionApi) => {
				const itemName = data.item?.name
				const paragraphLikeElements = new Set([
					'paragraph',
					'heading1',
					'heading2',
					'heading3',
					'heading4',
					'heading5',
					'heading6',
					'listItem',
					'blockQuote',
				])
				if (!paragraphLikeElements.has(itemName)) return

				const viewWriter = conversionApi.writer
				const viewElement = conversionApi.mapper.toViewElement(data.item)
				
				if (!viewElement) return
				
				const htmlAttributes = data.attributeNewValue
				const styles = htmlAttributes?.styles || {}
				
				// Список всех возможных стилей абзаца
				const paragraphStyleKeys = [
					'margin-left', 'margin-right', 'margin-top', 'margin-bottom',
					'text-indent', 'line-height', 'text-align'
				]
				
				// Удаляем все старые стили абзаца
				paragraphStyleKeys.forEach(key => {
					if (viewElement.hasStyle(key)) {
						viewWriter.removeStyle(key, viewElement)
					}
				})
				
				// Применяем новые стили
				if (Object.keys(styles).length > 0) {
					Object.entries(styles).forEach(([key, value]) => {
						viewWriter.setStyle(key, value, viewElement)
					})
				}
			}, { priority: 'low' })
		})

		const syncToolbarStateOnly = () => {
			syncToolbarState()
		}
		syncToolbarStateOnly()
		editor.model.document.on('change:selection', syncToolbarStateOnly)
		editor.model.document.selection.on('change:range', syncToolbarStateOnly)
		editor.editing.view.document.on('selectionChangeDone', () => {
			setTimeout(syncToolbarStateOnly, 0)
		})
		editor.editing.view.document.on('mouseup', () => {
			setTimeout(syncToolbarStateOnly, 0)
		})
		editor.editing.view.document.on('keyup', syncToolbarStateOnly)
		editor.editing.view.document.on('focus', syncToolbarStateOnly)
		
		// Удаляем badge (powered by)
		const removeBadge = () => {
			document.querySelectorAll('.ck-powered-by, [class*="powered-by"]').forEach(el => el.remove())
		}
		setTimeout(removeBadge, 50)
		setTimeout(removeBadge, 200)
		setTimeout(removeBadge, 1000)
		
		// Observer для badge
		const observer = new MutationObserver(() => {
			document.querySelectorAll('.ck-powered-by').forEach(el => el.remove())
		})
		observer.observe(document.body, { childList: true, subtree: true })
		editorInstance._badgeObserver = observer
		
		// Сворачиваемые секции
		setTimeout(() => markHeadingsAsCollapsible(), 500)
		editor.model.document.on('change:data', () => setTimeout(markHeadingsAsCollapsible, 100))
		
		// Клик по заголовкам
		const editorEl = document.querySelector('.ck-content')
		if (editorEl) {
			editorEl.addEventListener('click', (e) => {
				const heading = e.target.closest('h1[data-section-state], h2[data-section-state], h3[data-section-state], h4[data-section-state]')
				if (!heading) return
				const rect = heading.getBoundingClientRect()
				if (e.clientX - rect.left <= 30) {
					e.preventDefault()
					e.stopPropagation()
					toggleSection(heading)
				}
			})
		}
		
		updateToc()
	} catch (error) {
		console.error('Ошибка инициализации CKEditor:', error)
	}
}

// === DROPDOWNS ===
const closeAllDropdowns = () => {
	showLineSpacing.value = false
	showPageSettings.value = false
	showStats.value = false
	showFontFamily.value = false
	showFontSize.value = false
	showCaseMenu.value = false
}

const toggleLineSpacingDropdown = () => {
	console.log('🖱️ Клик по кнопке межстрочного интервала')
	const wasOpen = showLineSpacing.value
	closeAllDropdowns()
	showLineSpacing.value = !wasOpen
	console.log('📊 Dropdown состояние:', showLineSpacing.value)
	
	if (showLineSpacing.value) {
		nextTick(() => {
			// Ищем все dropdown-item внутри всех toolbar-dropdown
			const allButtons = document.querySelectorAll('.toolbar-dropdown .dropdown-item')
			console.log('🔍 Всего найдено dropdown-item на странице:', allButtons.length)
			
			// Фильтруем только видимые
			const visibleButtons = Array.from(allButtons).filter(btn => {
				const dropdown = btn.closest('.toolbar-dropdown')
				const isVisible = dropdown && window.getComputedStyle(dropdown).display !== 'none'
				console.log('🔍 Кнопка:', btn.textContent.trim(), 'Видима:', isVisible)
				return isVisible
			})
			
			console.log('✅ Видимых кнопок:', visibleButtons.length)
			
			visibleButtons.forEach((btn, index) => {
				const value = lineSpacingOptions[index]?.value
				console.log(`🎯 Навешиваем на кнопку ${index} (${btn.textContent.trim()}):`, value)
				
				btn.addEventListener('click', (e) => {
					console.log('💥 DIRECT CLICK EVENT:', value)
					e.preventDefault()
					e.stopPropagation()
					setLineSpacing(value)
				}, { once: true })
			})
		})
	}
}

const togglePageSettings = () => {
	const wasOpen = showPageSettings.value
	closeAllDropdowns()
	showPageSettings.value = !wasOpen
}

const toggleStats = () => {
	const wasOpen = showStats.value
	closeAllDropdowns()
	showStats.value = !wasOpen
}

const toggleFontFamilyDropdown = () => {
	const wasOpen = showFontFamily.value
	closeAllDropdowns()
	showFontFamily.value = !wasOpen
}

const toggleFontSizeDropdown = () => {
	const wasOpen = showFontSize.value
	closeAllDropdowns()
	showFontSize.value = !wasOpen
}

const toggleCaseDropdown = () => {
	const wasOpen = showCaseMenu.value
	closeAllDropdowns()
	showCaseMenu.value = !wasOpen
}

const toggleToc = () => {
	showToc.value = !showToc.value
	if (showToc.value) updateToc()
}

// === LINE SPACING ===
const setLineSpacing = (value) => {
	console.log('🔧 setLineSpacing вызван:', value)
	currentLineHeight.value = value
	showLineSpacing.value = false
	console.log('📦 Применяем стили...')
	applyParagraphStyles({ lineHeight: value })
	console.log('✅ Стили применены')
}

const getFormattingSelectionMarker = () => {
	return formattingSelectionRange
}

const clearFormattingSelectionTarget = () => {
	formattingSelectionRange = null
}

const runWithSafeSelectionAccess = (callback, fallbackValue = null) => {
	try {
		return callback()
	} catch (error) {
		const message = String(error?.message || '')
		const fullErrorText = String(error || '')
		if (message.includes('model-position-path-incorrect')) {
			console.warn('[RichTextEditor] Skip stale selection operation:', error)
			clearFormattingSelectionTarget()
			return fallbackValue
		}
		if (fullErrorText.includes('model-position-path-incorrect')) {
			console.warn('[RichTextEditor] Skip stale selection operation:', error)
			clearFormattingSelectionTarget()
			return fallbackValue
		}
		throw error
	}
}

const captureFormattingSelectionTarget = () => {
	if (!editorInstance) return false
	return runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		if (!selection || selection.isCollapsed) return false
		const firstRange = selection.getFirstRange()
		if (!firstRange || firstRange.isCollapsed) return false

	let hasTextInRange = false
	for (const item of Array.from(firstRange.getItems())) {
		if (item.is('$textProxy')) {
			hasTextInRange = true
			break
		}
	}
	if (!hasTextInRange) {
		clearFormattingSelectionTarget()
		return false
	}

	try {
		formattingSelectionRange = firstRange.clone()
	} catch (error) {
		console.warn('[RichTextEditor] Не удалось сохранить диапазон выделения:', error)
		formattingSelectionRange = null
		return false
	}
	return true
	}, false)
}

const hasValidFormattingSelectionRange = () => {
	if (!formattingSelectionRange) return false
	return runWithSafeSelectionAccess(() => {
		const startParent = formattingSelectionRange.start.parent
		const endParent = formattingSelectionRange.end.parent
		return !!startParent && !!endParent
	}, false)
}

const getFormattingTargetRanges = () => {
	if (!editorInstance) return []
	const liveSelectionRanges = runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		if (selection && !selection.isCollapsed) {
			return Array.from(selection.getRanges())
		}
		return null
	}, null)
	if (liveSelectionRanges) {
		return liveSelectionRanges
	}
	if (!hasValidFormattingSelectionRange()) {
		clearFormattingSelectionTarget()
		return []
	}
	return [formattingSelectionRange]
}

const rememberSelectionForFormatting = () => {
	const captured = captureFormattingSelectionTarget()
	if (!captured && editorInstance?.editing.view.document.isFocused) {
		clearFormattingSelectionTarget()
	}
	syncToolbarState()
}

const ensureFormattingSelection = () => {
	if (!editorInstance) return false
	const hasActiveSelection = runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		return !!selection && !selection.isCollapsed
	}, false)
	if (hasActiveSelection) return true
	if (!formattingSelectionRange) return false

	try {
		editorInstance.model.change(writer => {
			writer.setSelection(formattingSelectionRange)
		})
	} catch (error) {
		console.warn('[RichTextEditor] Не удалось восстановить сохранённое выделение:', error)
		formattingSelectionRange = null
		return false
	}
	return true
}

const selectionHasAttributeInRanges = (selection, attrName) => {
	if (!selection) return false
	return runWithSafeSelectionAccess(() => {
		if (selection.getAttribute(attrName)) return true
		for (const range of Array.from(selection.getRanges())) {
			for (const item of Array.from(range.getItems())) {
				if (!item.is('$textProxy')) continue
				if (item.getAttribute(attrName)) return true
			}
		}
		return false
	}, false)
}

const toggleBoldOnFormattingSelection = () => {
	if (!editorInstance) return
	if (!ensureFormattingSelection()) return
	runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		if (!selection || selection.isCollapsed) return

		const shouldApplyBold = !selectionHasAttributeInRanges(selection, 'bold')
		editorInstance.model.change(writer => {
			for (const range of Array.from(selection.getRanges())) {
				if (shouldApplyBold) {
					writer.setAttribute('bold', true, range)
				} else {
					writer.removeAttribute('bold', range)
				}
			}
		})
	}, null)
	captureFormattingSelectionTarget()
	editorInstance.editing.view.focus()
	syncToolbarState()
}

const getAttributeFromSelection = (attrName) => {
	if (!editorInstance) return null
	return runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		if (!selection) return null

		const selectionAttribute = selection.getAttribute(attrName)
		if (selectionAttribute) return selectionAttribute

		const firstPosition = selection.getFirstPosition()
		const firstPositionValue = firstPosition ? (
			firstPosition.textNode?.getAttribute(attrName) ||
			firstPosition.parent?.getAttribute(attrName) ||
			null
		) : null
		if (firstPositionValue) return firstPositionValue

		for (const range of getFormattingTargetRanges()) {
			for (const item of Array.from(range.getItems())) {
				if (!item.is('$textProxy')) continue
				const itemValue = item.getAttribute(attrName)
				if (itemValue) return itemValue
				const parentValue = item.parent?.getAttribute?.(attrName)
				if (parentValue) return parentValue
			}
			const startParentValue = range.start.parent?.getAttribute?.(attrName)
			if (startParentValue) return startParentValue
		}

		return null
	}, null)
}

const getCurrentFontFamilyFromSelection = () => {
	return getAttributeFromSelection('fontFamily')
}

const resolveFontFamilyLabel = (value) => {
	if (!value || value === 'default') return 'По умолчанию'
	const option = fontFamilyOptions.find(o => o.value === value)
	return option ? option.label : value
}

const getFontFamilyPreviewStyle = (value) => {
	if (!value || value === 'default') return {}
	return { fontFamily: value }
}

const applyFontFamily = (value) => {
	if (!editorInstance) return
	ensureFormattingSelection()
	editorInstance.execute('fontFamily', { value })
	captureFormattingSelectionTarget()
	
	currentFontFamily.value = value
	
	// Обновляем input
	showFontFamily.value = false
	editorInstance.editing.view.focus()
}

const applyFontSize = (value) => {
	if (!editorInstance) return
	ensureFormattingSelection()
	editorInstance.execute('fontSize', { value })
	captureFormattingSelectionTarget()
	currentFontSize.value = normalizeFontSizeValue(value)
	
	// Обновляем input
	showFontSize.value = false
	editorInstance.editing.view.focus()
}

const getCurrentFontSizeFromSelection = () => {
	if (!editorInstance) return null
	const fontSize = getAttributeFromSelection('fontSize')
	
	if (fontSize) {
		const match = String(fontSize).match(/(\d+)/)
		return match ? match[1] : null
	}
	
	return null
}

const clearFormatting = () => {
	if (!editorInstance) return
	const defaultFontFamily = 'Times New Roman, Times, serif'
	const defaultFontSize = '12pt'
	const defaultAlignment = 'justify'

	const headingCommand = editorInstance.commands.get('heading')
	if (headingCommand?.isEnabled) {
		editorInstance.execute('heading', { value: 'paragraph' })
	}

	editorInstance.execute('removeFormat')

	runWithSafeSelectionAccess(() => {
		editorInstance.model.change(writer => {
			const blocks = Array.from(editorInstance.model.document.selection.getSelectedBlocks())
			blocks.forEach(block => {
				if (block.hasAttribute('htmlAttributes')) {
					writer.removeAttribute('htmlAttributes', block)
				}
			})
		})
	}, null)

	editorInstance.execute('alignment', { value: defaultAlignment })
	editorInstance.execute('fontFamily', { value: defaultFontFamily })
	editorInstance.execute('fontSize', { value: defaultFontSize })
	currentFontFamily.value = defaultFontFamily
	currentFontSize.value = normalizeFontSizeValue(defaultFontSize)
	editorInstance.editing.view.focus()
}

const toSentenceCase = (text, state) => {
	const lower = text.toLocaleLowerCase()
	let result = ''
	let capitalizeNext = state?.capitalizeNext ?? true
	for (const ch of lower) {
		if (/\p{L}/u.test(ch)) {
			result += capitalizeNext ? ch.toLocaleUpperCase() : ch
			capitalizeNext = false
		} else {
			result += ch
			if (/[.!?\u2026]/.test(ch) || ch === '\n' || ch === '\r') {
				capitalizeNext = true
			}
		}
	}
	if (state) state.capitalizeNext = capitalizeNext
	return result
}

const applyCase = (value) => {
	if (!editorInstance) return
	runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		if (selection.isCollapsed) return
		const sentenceState = { capitalizeNext: true }
		editorInstance.model.change(writer => {
			for (const range of selection.getRanges()) {
				for (const item of Array.from(range.getItems())) {
					if (!item.is('$textProxy')) continue
					const original = item.data
					let next = original
					if (value === 'upper') next = original.toLocaleUpperCase()
					if (value === 'lower') next = original.toLocaleLowerCase()
					if (value === 'title') {
						next = original.replace(/\S+/g, word =>
							word.charAt(0).toLocaleUpperCase() + word.slice(1).toLocaleLowerCase()
						)
					}
					if (value === 'sentence') next = toSentenceCase(original, sentenceState)
					if (next === original) continue
					const parent = item.parent
					if (!parent) continue
					const offset = item.startOffset
					const attrs = Array.from(item.getAttributes())
					const position = writer.createPositionAt(parent, offset)
					writer.remove(item)
					writer.insertText(next, attrs, position)
				}
			}
		})
	}, null)
	showCaseMenu.value = false
	editorInstance.editing.view.focus()
}

// Сохранённое выделение для диалога абзаца
let savedSelectionRanges = null

const openParagraphDialog = () => {
	if (!editorInstance) return
	closeAllDropdowns()
	
	// Сохраняем ranges текущего выделения
	const selectionData = runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		return {
			ranges: Array.from(selection.getRanges()),
			blocks: Array.from(selection.getSelectedBlocks()),
		}
	}, null)
	if (!selectionData) {
		savedSelectionRanges = null
		return
	}
	const { ranges, blocks } = selectionData
	
	savedSelectionRanges = ranges.map(range => {
		const rangeData = {
			start: range.start.path.slice(),
			end: range.end.path.slice()
		}
		return rangeData
	})
	
	// Читаем текущие стили из первого блока
	const firstBlock = blocks[0]
	const htmlAttributes = firstBlock?.getAttribute('htmlAttributes')
	const styles = htmlAttributes?.styles || {}
	
	// Конвертируем px обратно в см/пт для диалога
	const pxToCm = (px) => parseFloat((parseFloat(px) / 37.8).toFixed(2))
	const pxToPt = (px) => parseFloat((parseFloat(px) / 1.33).toFixed(1))
	
	const initialSettings = {}
	
	// Отступы слева/справа
	if (styles['margin-left']) {
		initialSettings.indentLeft = pxToCm(styles['margin-left'])
		initialSettings.indentLeftUnit = 'cm'
	}
	if (styles['margin-right']) {
		initialSettings.indentRight = pxToCm(styles['margin-right'])
		initialSettings.indentRightUnit = 'cm'
	}
	
	// Первая строка
	if (styles['text-indent']) {
		const value = parseFloat(styles['text-indent'])
		if (value > 0) {
			initialSettings.firstLineType = 'indent'
			initialSettings.firstLineValue = pxToCm(styles['text-indent'])
			initialSettings.firstLineUnit = 'cm'
		} else if (value < 0) {
			initialSettings.firstLineType = 'hanging'
			initialSettings.firstLineValue = pxToCm(Math.abs(value))
			initialSettings.firstLineUnit = 'cm'
		}
	}
	
	// Интервалы до/после
	if (styles['margin-top']) {
		initialSettings.spacingBefore = pxToPt(styles['margin-top'])
		initialSettings.spacingBeforeUnit = 'pt'
	}
	if (styles['margin-bottom']) {
		initialSettings.spacingAfter = pxToPt(styles['margin-bottom'])
		initialSettings.spacingAfterUnit = 'pt'
	}
	
	// Межстрочный интервал
	if (styles['line-height']) {
		const lineHeight = parseFloat(styles['line-height'])
		if (lineHeight === 1) initialSettings.lineSpacingType = 'single'
		else if (lineHeight === 1.15) initialSettings.lineSpacingType = '1.15'
		else if (lineHeight === 1.5) initialSettings.lineSpacingType = '1.5'
		else if (lineHeight === 2) initialSettings.lineSpacingType = 'double'
		else {
			initialSettings.lineSpacingType = 'multiple'
			initialSettings.lineSpacingValue = lineHeight
		}
	}
	
	currentDialogSettings.value = initialSettings
	showParagraphDialog.value = true
}

const closeParagraphDialog = () => {
	showParagraphDialog.value = false
	savedSelectionRanges = null
}

const applyParagraphSettings = (settings) => {
	if (!editorInstance) return
	
	// Восстанавливаем выделение перед применением
	if (savedSelectionRanges && savedSelectionRanges.length > 0) {
		try {
			editorInstance.model.change(writer => {
				const root = editorInstance.model.document.getRoot()
				const ranges = savedSelectionRanges.map(rangeData => {
					const start = writer.createPositionFromPath(root, rangeData.start)
					const end = writer.createPositionFromPath(root, rangeData.end)
					return writer.createRange(start, end)
				})
				writer.setSelection(ranges)
			})
		} catch (error) {
			console.error('Ошибка восстановления выделения:', error)
		}
	}
	
	// Конвертация единиц в px
	const convertToPx = (value, unit) => {
		if (unit === 'cm') return value * 37.8
		if (unit === 'pt') return value * 1.33
		return value
	}
	
	const leftPx = convertToPx(settings.indentLeft, settings.indentLeftUnit)
	const rightPx = convertToPx(settings.indentRight, settings.indentRightUnit)
	const beforePx = convertToPx(settings.spacingBefore, settings.spacingBeforeUnit)
	const afterPx = convertToPx(settings.spacingAfter, settings.spacingAfterUnit)
	
	// Первая строка
	let firstLinePx = 0
	if (settings.firstLineType === 'indent') {
		firstLinePx = convertToPx(settings.firstLineValue, settings.firstLineUnit)
	} else if (settings.firstLineType === 'hanging') {
		firstLinePx = -convertToPx(settings.firstLineValue, settings.firstLineUnit)
	}
	
	// Межстрочный интервал
	let lineHeight = 1.15
	if (settings.lineSpacingType === 'single') lineHeight = 1
	else if (settings.lineSpacingType === '1.15') lineHeight = 1.15
	else if (settings.lineSpacingType === '1.5') lineHeight = 1.5
	else if (settings.lineSpacingType === 'double') lineHeight = 2
	else if (settings.lineSpacingType === 'multiple') lineHeight = settings.lineSpacingValue
	else if (settings.lineSpacingType === 'exact') lineHeight = `${convertToPx(settings.lineSpacingValue, 'pt')}px`
	else if (settings.lineSpacingType === 'minimum') lineHeight = Math.max(1, convertToPx(settings.lineSpacingValue, 'pt') / 16)
	
	applyParagraphStyles({
		indentLeft: leftPx,
		indentRight: rightPx,
		firstLineIndent: firstLinePx,
		spacingBefore: beforePx,
		spacingAfter: afterPx,
		lineHeight: lineHeight
	})
	
	currentLineHeight.value = String(lineHeight)
	savedSelectionRanges = null
	editorInstance.editing.view.focus()
}

const applyParagraphStyles = ({
	indentLeft,
	indentRight,
	firstLineIndent,
	spacingBefore,
	spacingAfter,
	lineHeight
} = {}) => {
	console.log('🎨 applyParagraphStyles вызван:', { indentLeft, indentRight, firstLineIndent, spacingBefore, spacingAfter, lineHeight })
	
	if (!editorInstance) {
		console.error('❌ editorInstance не найден')
		return
	}
	
	const paragraphLikeElements = new Set([
		'paragraph',
		'heading1',
		'heading2',
		'heading3',
		'heading4',
		'heading5',
		'heading6',
		'listItem',
		'blockQuote',
	])
	const blocks = runWithSafeSelectionAccess(() => (
		Array.from(editorInstance.model.document.selection.getSelectedBlocks())
			.filter(block => paragraphLikeElements.has(block.name))
	), [])
	console.log('📦 Выбрано блоков:', blocks.length)
	
	if (!blocks.length) {
		console.warn('⚠️ Нет выбранных блоков')
		return
	}
	
	editorInstance.model.change(writer => {
		blocks.forEach((block, index) => {
			console.log(`🔧 Обработка блока ${index + 1}:`, block.name)
			
			// Получаем или создаём htmlAttributes
			const htmlAttributes = block.getAttribute('htmlAttributes') || {}
			const styles = { ...(htmlAttributes.styles || {}) }
			
			console.log('📋 Текущие стили:', styles)

			// Применяем стили
			if (indentLeft !== undefined) {
				if (indentLeft > 0) {
					styles['margin-left'] = `${indentLeft}px`
				} else {
					delete styles['margin-left']
				}
			}
			
			if (indentRight !== undefined) {
				if (indentRight > 0) {
					styles['margin-right'] = `${indentRight}px`
				} else {
					delete styles['margin-right']
				}
			}
			
			if (firstLineIndent !== undefined) {
				if (firstLineIndent !== 0) {
					styles['text-indent'] = `${firstLineIndent}px`
				} else {
					delete styles['text-indent']
				}
			}
			
			if (spacingBefore !== undefined) {
				if (spacingBefore > 0) {
					styles['margin-top'] = `${spacingBefore}px`
				} else {
					delete styles['margin-top']
				}
			}
			
			if (spacingAfter !== undefined) {
				if (spacingAfter > 0) {
					styles['margin-bottom'] = `${spacingAfter}px`
				} else {
					delete styles['margin-bottom']
				}
			}
			
			if (lineHeight !== undefined) {
				console.log('📏 Применяем line-height:', lineHeight, typeof lineHeight)
				if (typeof lineHeight === 'number') {
					styles['line-height'] = String(lineHeight)
				} else {
					styles['line-height'] = lineHeight
				}
			}
			
			console.log('✨ Новые стили:', styles)
			
			// Обновляем атрибуты
			const nextAttributes = { ...htmlAttributes, styles }
			const hasStyles = Object.keys(styles).length > 0
			
			console.log('💾 Сохраняем атрибуты:', nextAttributes, 'hasStyles:', hasStyles)
			
			if (hasStyles) {
				writer.setAttribute('htmlAttributes', nextAttributes, block)
				console.log('✅ Атрибуты установлены на блок')
			} else if (block.hasAttribute('htmlAttributes')) {
				writer.removeAttribute('htmlAttributes', block)
				console.log('🗑️ Атрибуты удалены с блока')
			}
		})
	})
	
	console.log('🔄 Принудительное обновление view...')
	// Принудительно обновляем view
	editorInstance.editing.view.forceRender()
	console.log('✅ applyParagraphStyles завершён')
}

const syncToolbarState = () => {
	if (!editorInstance) return

	runWithSafeSelectionAccess(() => {
		const selection = editorInstance.model.document.selection
		const hasFormattingTarget = !selection.isCollapsed || !!getFormattingSelectionMarker()
		const commandFontFamily = editorInstance.commands.get('fontFamily')?.value
		const selectionFontFamily = getCurrentFontFamilyFromSelection()
		const resolvedFontFamily = hasFormattingTarget
			? (selectionFontFamily || commandFontFamily || 'default')
			: (commandFontFamily || selectionFontFamily || 'default')
		currentFontFamily.value = resolvedFontFamily

		const commandFontSize = editorInstance.commands.get('fontSize')?.value
		const selectionFontSize = getCurrentFontSizeFromSelection()
		const resolvedFontSize = hasFormattingTarget
			? (selectionFontSize ? `${selectionFontSize}pt` : (commandFontSize || 'default'))
			: (commandFontSize || (selectionFontSize ? `${selectionFontSize}pt` : 'default'))
		currentFontSize.value = normalizeFontSizeValue(resolvedFontSize)

		if (!selection.isCollapsed) {
			captureFormattingSelectionTarget()
		}
	}, null)
}

const normalizeFontSizeValue = (value) => {
	if (!value) return 'default'
	return String(value).replace(/(px|pt)/gi, '')
}

// === PAGE SETTINGS ===
const setPageWidth = (width) => {
	pageWidth.value = width
	showPageSettings.value = false
}

const getPageWidthLabel = () => {
	const option = pageWidthOptions.find(o => o.value === pageWidth.value)
	return option ? option.label.split(' ')[0] : 'A4'
}

// === FULLSCREEN ===
const toggleFullscreen = () => {
	isFullscreen.value = !isFullscreen.value
	document.body.style.overflow = isFullscreen.value ? 'hidden' : ''
}

// === TOC ===
const updateToc = () => {
	const editorEl = document.querySelector('.ck-content')
	if (!editorEl) return
	
	const headings = editorEl.querySelectorAll('h1, h2, h3, h4')
	const headingLevels = { H1: 1, H2: 2, H3: 3, H4: 4 }
	const items = []
	
	headings.forEach((heading, index) => {
		const text = heading.textContent.replace(/^[▸▾]\s*/, '').trim()
		const level = headingLevels[heading.tagName]
		const id = heading.id || `heading-${index}`
		if (!heading.id) heading.id = id
		items.push({ text, level, id })
	})
	
	tocItems.value = items
}

const scrollToHeading = (id) => {
	const element = document.getElementById(id)
	if (element) {
		element.scrollIntoView({ behavior: 'smooth', block: 'start' })
		element.classList.add('heading-highlight')
		setTimeout(() => element.classList.remove('heading-highlight'), 2000)
	}
}

// === SECTIONS ===
const markHeadingsAsCollapsible = () => {
	const editorEl = document.querySelector('.ck-content')
	if (!editorEl) return
	editorEl.querySelectorAll('h1, h2, h3, h4').forEach(heading => {
		if (!heading.hasAttribute('data-section-state')) {
			heading.setAttribute('data-section-state', 'expanded')
		}
	})
}

const toggleSection = (heading) => {
	const isCollapsed = heading.getAttribute('data-section-state') === 'collapsed'
	const headingLevel = parseInt(heading.tagName.substring(1))
	let currentElement = heading.nextElementSibling
	const sectionElements = []
	
	while (currentElement) {
		if (/^H[1-4]$/.test(currentElement.tagName)) {
			const currentLevel = parseInt(currentElement.tagName.substring(1))
			if (currentLevel <= headingLevel) break
		}
		sectionElements.push(currentElement)
		currentElement = currentElement.nextElementSibling
	}
	
	if (isCollapsed) {
		heading.setAttribute('data-section-state', 'expanded')
		sectionElements.forEach(el => el.style.display = '')
	} else {
		heading.setAttribute('data-section-state', 'collapsed')
		sectionElements.forEach(el => el.style.display = 'none')
	}
}

const collapseAllSections = () => {
	const editorEl = document.querySelector('.ck-content')
	if (!editorEl) return
	editorEl.querySelectorAll('h1, h2, h3, h4').forEach(heading => {
		if (heading.getAttribute('data-section-state') === 'expanded') {
			toggleSection(heading)
		}
	})
}

const expandAllSections = () => {
	const editorEl = document.querySelector('.ck-content')
	if (!editorEl) return
	editorEl.querySelectorAll('h1, h2, h3, h4').forEach(heading => {
		if (heading.getAttribute('data-section-state') === 'collapsed') {
			toggleSection(heading)
		}
	})
}

// === PRINT ===
const printDocument = () => {
	const content = editorInstance?.getData() || ''
	const printWindow = window.open('', '_blank')
	if (!printWindow) {
		alert('Разрешите всплывающие окна для печати')
		return
	}
	printWindow.document.write(`
		<!DOCTYPE html>
		<html>
		<head>
			<title>Печать</title>
			<style>
				@page { size: A4; margin: 2cm; }
				body { font-family: Arial, sans-serif; font-size: 12pt; line-height: ${currentLineHeight.value}; color: #000; }
				h1 { font-size: 18pt; margin: 18pt 0 12pt; }
				h2 { font-size: 16pt; margin: 16pt 0 10pt; }
				h3 { font-size: 14pt; margin: 14pt 0 8pt; }
				p { margin: 0 0 12pt; }
				table { border-collapse: collapse; width: 100%; }
				td, th { border: 1px solid #000; padding: 6pt; }
				img { max-width: 100%; }
			</style>
		</head>
		<body>${content}</body>
		</html>
	`)
	printWindow.document.close()
	printWindow.print()
}

// === GLOBAL HANDLERS ===
const handleGlobalKeydown = (e) => {
	if (e.key === 'F11') {
		e.preventDefault()
		toggleFullscreen()
	}
	if (e.key === 'Escape' && isFullscreen.value) {
		toggleFullscreen()
	}
}

const handleGlobalClick = (e) => {
	const isDropdown = e.target.closest('.toolbar-dropdown-wrapper')
	const isDropdownItem = e.target.closest('.dropdown-item')
	
	// Не закрываем если клик внутри dropdown или по элементу dropdown
	if (!isDropdown && !isDropdownItem) {
		closeAllDropdowns()
	}
}

// === WATCH ===
watch(() => props.modelValue, (newValue) => {
	const normalizedValue = normalizeLegacyUploadedVideoMarkup(newValue || '')
	latestExternalModelValue = normalizedValue

	if (latestEditorData === normalizedValue) {
		clearDraftSnapshot()
		draftSaveState.value = 'idle'
		draftSavedAt.value = null
	}

	if (!editorInstance) return

	let currentData = ''
	try {
		currentData = editorInstance.getData()
	} catch (error) {
		console.warn('[RichTextEditor] Failed to read editor data in model watcher:', error)
		return
	}

	if (currentData === normalizedValue) return

	try {
		isApplyingExternalModelValue = true
		editorInstance.setData(normalizedValue)
		latestEditorData = normalizedValue
		if (!hasPotentialUnsavedChanges()) {
			draftSaveState.value = 'idle'
			draftSavedAt.value = null
		}
	} catch (error) {
		console.error('[RichTextEditor] Failed to apply external model value:', error)
	} finally {
		isApplyingExternalModelValue = false
	}
})
</script>

<style scoped>
.rich-text-editor {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100vh;
	min-height: 0;
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.rich-text-editor.is-fullscreen {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	height: auto;
	z-index: 9999;
	background: #f5f5f5;
	padding: 20px;
}

.editor-label {
	font-size: 14px;
	font-weight: 500;
	color: #333;
	margin-bottom: 10px;
}

/* Toolbar */
.editor-toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
	padding: 8px 12px;
	background: #fff;
	border: 1px solid #ddd;
	border-radius: 4px 4px 0 0;
}

.editor-toolbar--secondary {
	border-top: none;
	border-radius: 0;
	background: #fafafa;
	padding: 6px 12px;
}

.toolbar-group {
	display: flex;
	align-items: center;
	gap: 4px;
}

.toolbar-group-label {
	font-size: 12px;
	color: #666;
	margin-right: 4px;
}

.toolbar-divider {
	width: 1px;
	height: 24px;
	background: #ddd;
	margin: 0 6px;
}

.toolbar-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 1px solid transparent;
	border-radius: 4px;
	background: transparent;
	cursor: pointer;
	transition: all 0.15s;
}

.toolbar-btn:hover {
	background: #e8e8e8;
	border-color: #ccc;
}

.toolbar-btn.is-active {
	background: #e0f0ff;
	border-color: #4a9eff;
	color: #0066cc;
}

.toolbar-btn svg {
	width: 18px;
	height: 18px;
}

.toolbar-btn--with-label {
	width: auto;
	padding: 0 8px;
	gap: 4px;
}

.btn-value {
	font-size: 11px;
	color: #666;
}

/* Dropdowns */
.toolbar-dropdown-wrapper {
	position: relative;
}

/* Font Size Input */
.font-size-wrapper {
	display: flex;
	align-items: center;
	gap: 2px;
}

.font-size-unit {
	font-size: 12px;
	color: #666;
	min-width: 18px;
	text-align: center;
	user-select: none;
}

.font-size-input {
	width: 50px;
	height: 32px;
	padding: 4px 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 13px;
	text-align: center;
	transition: all 0.2s;
}

/* Font Family Input */
.font-family-wrapper {
	display: flex;
	align-items: center;
	gap: 2px;
}

.font-family-input {
	width: 160px;
	height: 32px;
	padding: 4px 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 13px;
	transition: all 0.2s;
}

.font-family-input:hover,
.font-size-input:hover {
	border-color: #999;
}

.font-family-input:focus,
.font-size-input:focus {
	outline: none;
	border-color: #0066cc;
	box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
}

.toolbar-btn--dropdown {
	width: 24px;
	height: 32px;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #ddd;
	border-radius: 4px;
	background: #fff;
	cursor: pointer;
	transition: all 0.2s;
}

.toolbar-btn--selector {
	width: auto;
	min-width: 120px;
	padding: 0 8px;
	gap: 8px;
	justify-content: space-between;
}

.font-family-wrapper .toolbar-btn--selector {
	min-width: 210px;
}

.font-size-wrapper .toolbar-btn--selector {
	min-width: 84px;
}

.selector-value {
	font-size: 13px;
	color: #333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	text-align: left;
}

.toolbar-btn--dropdown:hover {
	background: #f5f5f5;
	border-color: #999;
}

.toolbar-btn--dropdown svg {
	width: 14px;
	height: 14px;
	flex-shrink: 0;
}

.font-size-dropdown {
	max-height: 300px;
	overflow-y: auto;
}

.font-family-dropdown {
	max-height: 320px;
	overflow-y: auto;
}

.toolbar-dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	margin-top: 4px;
	min-width: 160px;
	background: #fff;
	border: 1px solid #ddd;
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0,0,0,0.15);
	z-index: 1000;
	padding: 4px 0;
}

.dropdown-item {
	display: block;
	width: 100%;
	padding: 8px 12px;
	border: none;
	background: none;
	text-align: left;
	cursor: pointer;
	font-size: 13px;
}

.dropdown-item:hover {
	background: #f0f0f0;
}

.dropdown-item.is-active {
	background: #e0f0ff;
	color: #0066cc;
}

.dropdown-header {
	padding: 6px 12px;
	font-size: 11px;
	font-weight: 600;
	color: #666;
	text-transform: uppercase;
}

.dropdown-divider {
	height: 1px;
	background: #eee;
	margin: 4px 0;
}

.dropdown-checkbox {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	cursor: pointer;
}

.dropdown-checkbox input {
	margin: 0;
}

/* Stats dropdown */
.stats-dropdown {
	min-width: 220px;
	padding: 8px;
}

.stats-item {
	display: flex;
	justify-content: space-between;
	padding: 6px 8px;
	border-bottom: 1px solid #eee;
}

.stats-item:last-child {
	border-bottom: none;
}

.stats-label {
	font-size: 12px;
	color: #666;
}

.stats-value {
	font-size: 12px;
	font-weight: 600;
	color: #333;
}

/* Main container */
.editor-main-container {
	display: flex;
	flex: 1;
	min-height: 0;
	background: #f5f5f5;
	overflow: hidden;
	overflow-anchor: none;
}

/* TOC Panel */
.toc-panel {
	width: 250px;
	background: #fff;
	border-right: 1px solid #ddd;
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
}

.toc-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	border-bottom: 1px solid #eee;
}

.toc-header h4 {
	margin: 0;
	font-size: 14px;
	font-weight: 600;
}

.toc-close {
	background: none;
	border: none;
	font-size: 18px;
	cursor: pointer;
	color: #666;
	padding: 0;
	width: 24px;
	height: 24px;
}

.toc-content {
	flex: 1;
	overflow-y: auto;
	padding: 8px 0;
}

.toc-item {
	padding: 8px 16px;
	cursor: pointer;
	font-size: 13px;
	color: #333;
	transition: background 0.15s;
}

.toc-item:hover {
	background: #f0f0f0;
}

.toc-item--h1 { padding-left: 16px; font-weight: 600; }
.toc-item--h2 { padding-left: 28px; }
.toc-item--h3 { padding-left: 40px; font-size: 12px; }
.toc-item--h4 { padding-left: 52px; font-size: 12px; color: #666; }

.toc-empty {
	padding: 16px;
	text-align: center;
	color: #999;
	font-size: 13px;
}

/* Page container */
.editor-page-container {
	flex: 1;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 20px;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
}

.is-fullscreen .editor-page-container {
	margin: 0 auto;
}

.paper-container {
	background: white;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	border-radius: 8px;
	padding: 60px 80px;
	align-self: flex-start;
	min-height: 600px;
	height: auto;
	overflow: visible;
	width: 100%;
	max-width: 900px;
}

.is-fullscreen .paper-container {
	min-height: clamp(700px, calc(100vh - 240px), 1400px);
	min-height: clamp(700px, calc(100dvh - 240px), 1400px);
}

.is-fullscreen.page-mode .paper-container {
	box-shadow: 0 0 20px rgba(0,0,0,0.15);
}

.editor-container {
	background: white;
}

/* Status bar */
.editor-status {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 9px 16px;
	padding-right: 44px;
	background: linear-gradient(180deg, #fbfdff 0%, #f4f8fc 100%);
	border: 1px solid #ddd;
	border-top: none;
	border-radius: 0 0 4px 4px;
	font-size: 12px;
	color: #666;
	position: relative;
	transition: box-shadow 0.16s ease, border-color 0.16s ease;
}

.is-fullscreen .editor-main-container {
	padding-bottom: 56px;
}

.is-fullscreen .editor-status {
	position: fixed;
	left: 20px;
	right: 20px;
	bottom: 20px;
	z-index: 10010;
	border-top: 1px solid #ddd;
	border-radius: 4px;
}

.editor-status--resizing {
	border-color: #86b9f8;
	box-shadow: inset 0 0 0 1px rgba(0, 102, 204, 0.16);
}

.status-left {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.status-divider {
	color: #ccc;
}

.status-right {
	display: flex;
	align-items: center;
	gap: 8px;
}

.status-size {
	font-weight: 600;
	color: #345;
}

.status-sync {
	font-size: 11px;
	padding: 2px 8px;
	border-radius: 999px;
	background: #eef2ff;
	color: #3730a3;
	white-space: nowrap;
}

.status-sync--idle,
.status-sync--saved {
	background: #ecfdf3;
	color: #166534;
}

.status-sync--dirty {
	background: #fff7ed;
	color: #9a3412;
}

.status-sync--saving {
	background: #eff6ff;
	color: #1d4ed8;
}

.status-sync--error {
	background: #fef2f2;
	color: #b91c1c;
}

.status-badge {
	padding: 2px 8px;
	background: #e8e8e8;
	border-radius: 4px;
	font-size: 11px;
}

.status-badge--page {
	background: #e0f0ff;
	color: #0066cc;
}

.editor-status-resizer {
	position: absolute;
	right: 7px;
	bottom: 3px;
	width: 24px;
	height: 24px;
	padding: 0;
	border: 1px solid transparent;
	border-radius: 6px;
	background: rgba(255, 255, 255, 0.75);
	cursor: ns-resize;
	opacity: 0.92;
	transition: opacity 0.14s ease, border-color 0.14s ease, background 0.14s ease;
}

.editor-status-resizer::before {
	content: '';
	position: absolute;
	right: 6px;
	bottom: 6px;
	width: 10px;
	height: 10px;
	border-right: 2px solid #7f8898;
	border-bottom: 2px solid #7f8898;
}

.editor-status-resizer:hover {
	opacity: 1;
	border-color: #b9d2f8;
	background: rgba(255, 255, 255, 0.95);
}

.editor-status-resizer:focus-visible {
	outline: none;
	border-color: #5b9df5;
	box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.rich-text-editor.is-resizing .editor-status-resizer {
	background: #fff;
	border-color: #8ebcf8;
	opacity: 1;
}

/* CKEditor overrides */
:deep(.ck-powered-by),
:deep([class*="powered-by"]) {
	display: none !important;
}

:deep(.ck.ck-editor__top) {
	display: none;
}

:deep(.ck.ck-editor__editable) {
	border: none !important;
	box-shadow: none !important;
}

:deep(.ck-content) {
	min-height: 400px;
	padding: 20px 0;
}

:deep(.ck.ck-toolbar .ck-button[data-rte-tooltip]),
:deep(.ck.ck-toolbar .ck-dropdown__button[data-rte-tooltip]) {
	position: relative;
}

:deep(.ck.ck-toolbar .ck-button[data-rte-tooltip]:hover::after),
:deep(.ck.ck-toolbar .ck-button[data-rte-tooltip]:focus-visible::after),
:deep(.ck.ck-toolbar .ck-dropdown__button[data-rte-tooltip]:hover::after),
:deep(.ck.ck-toolbar .ck-dropdown__button[data-rte-tooltip]:focus-visible::after) {
	content: attr(data-rte-tooltip);
	position: absolute;
	left: 50%;
	bottom: calc(100% + 8px);
	transform: translateX(-50%);
	padding: 6px 8px;
	border-radius: 6px;
	background: rgba(15, 23, 42, 0.96);
	color: #fff;
	font-size: 11px;
	line-height: 1.2;
	white-space: nowrap;
	pointer-events: none;
	z-index: 1200;
	box-shadow: 0 8px 16px rgba(15, 23, 42, 0.24);
}

:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-button[data-rte-tooltip]:hover::after),
:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-button[data-rte-tooltip]:focus-visible::after),
:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-dropdown__button[data-rte-tooltip]:hover::after),
:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-dropdown__button[data-rte-tooltip]:focus-visible::after) {
	bottom: calc(100% + 3px);
	white-space: normal;
	max-width: 220px;
	text-align: center;
	word-break: normal;
	line-height: 1.25;
}

:deep(.ck.ck-toolbar .ck-button[data-rte-tooltip]:hover::before),
:deep(.ck.ck-toolbar .ck-button[data-rte-tooltip]:focus-visible::before),
:deep(.ck.ck-toolbar .ck-dropdown__button[data-rte-tooltip]:hover::before),
:deep(.ck.ck-toolbar .ck-dropdown__button[data-rte-tooltip]:focus-visible::before) {
	content: '';
	position: absolute;
	left: 50%;
	bottom: calc(100% + 2px);
	transform: translateX(-50%);
	border-left: 5px solid transparent;
	border-right: 5px solid transparent;
	border-top: 6px solid rgba(15, 23, 42, 0.96);
	pointer-events: none;
	z-index: 1200;
}

:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-button[data-rte-tooltip]:hover::before),
:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-button[data-rte-tooltip]:focus-visible::before),
:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-dropdown__button[data-rte-tooltip]:hover::before),
:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-dropdown__button[data-rte-tooltip]:focus-visible::before) {
	bottom: calc(100% - 2px);
}

:deep(.ck.ck-balloon-panel.ck-toolbar-container) {
	margin-top: 6px;
}

:deep(.ck.ck-balloon-panel.ck-toolbar-container),
:deep(.ck.ck-balloon-panel.ck-toolbar-container .ck.ck-toolbar),
:deep(.ck.ck-balloon-panel.ck-toolbar-container .ck.ck-toolbar .ck-toolbar__items),
:deep(.ck.ck-toolbar.ck-toolbar_floating),
:deep(.ck.ck-toolbar.ck-toolbar_floating .ck-toolbar__items) {
	overflow: visible !important;
}

:deep(.ck.ck-tooltip),
:deep(.ck.ck-balloon-panel.ck-tooltip),
:deep(.ck-body-wrapper .ck.ck-tooltip),
:deep(.ck-body-wrapper .ck.ck-balloon-panel.ck-tooltip) {
	display: none !important;
	visibility: hidden !important;
	opacity: 0 !important;
	pointer-events: none !important;
}

/* Collapsible headings */
:deep(.ck-content h1[data-section-state]::before),
:deep(.ck-content h2[data-section-state]::before),
:deep(.ck-content h3[data-section-state]::before),
:deep(.ck-content h4[data-section-state]::before) {
	content: '▾';
	display: inline-block;
	width: 20px;
	cursor: pointer;
	color: #999;
	font-size: 0.8em;
	user-select: none;
	transition: color 0.2s;
	position: absolute;
	left: 0;
	top: 50%;
	transform: translateY(-50%);
}

:deep(.ck-content h1[data-section-state]),
:deep(.ck-content h2[data-section-state]),
:deep(.ck-content h3[data-section-state]),
:deep(.ck-content h4[data-section-state]) {
	position: relative;
	padding-left: 24px;
}

:deep(.ck-content h1[data-section-state="collapsed"]::before),
:deep(.ck-content h2[data-section-state="collapsed"]::before),
:deep(.ck-content h3[data-section-state="collapsed"]::before),
:deep(.ck-content h4[data-section-state="collapsed"]::before) {
	content: '▸';
}

:deep(.ck-content h1[data-section-state]:hover::before),
:deep(.ck-content h2[data-section-state]:hover::before),
:deep(.ck-content h3[data-section-state]:hover::before),
:deep(.ck-content h4[data-section-state]:hover::before) {
	color: #333;
}

:deep(.heading-highlight) {
	animation: highlight-pulse 2s ease-out;
}

@keyframes highlight-pulse {
	0% { background-color: #fff3cd; }
	100% { background-color: transparent; }
}
</style>

<!-- Глобальные стили для CKEditor (не scoped) -->
<style>
/* Скрываем кнопки "вставить параграф до/после блока" */
.ck .ck-widget__type-around,
.ck .ck-widget__type-around__button,
.ck .ck-widget__type-around__fake-caret,
.ck-widget__type-around,
.ck-widget__type-around__button,
.ck-widget__type-around__fake-caret {
	display: none !important;
	visibility: hidden !important;
	width: 0 !important;
	height: 0 !important;
	overflow: hidden !important;
	position: absolute !important;
	left: -9999px !important;
}

/* === КОНТЕЙНЕР === */
.ck-content {
	overflow-x: hidden;
	overflow-y: visible;
}

.ck-content ul,
.ck-content ol {
	list-style-position: outside;
	padding-left: 1.5em;
	margin: 0.5em 0;
}

.ck-content ul {
	list-style-type: disc;
}

.ck-content ol {
	list-style-type: decimal;
}

.ck-content li {
	margin: 0.2em 0;
}

.ck-content em,
.ck-content i {
	font-style: italic;
}

.ck-content strong,
.ck-content b {
	font-weight: 700 !important;
}

/* === Список задач === */
.ck-content .todo-list {
	padding-left: 1.5em;
	margin: 0.5em 0;
}

.ck-content .todo-list li {
	display: flex !important;
	align-items: center;
	gap: 8px;
	flex-wrap: nowrap;
	min-width: 0;
}

.ck-content .todo-list li > .todo-list__label {
	flex: 0 0 auto;
}

.ck-content .todo-list li > div {
	flex: 1 1 auto;
	margin: 0;
	min-width: 0;
	display: inline-block;
}

.ck-content .todo-list li > p {
	margin: 0;
	flex: 1 1 auto;
	min-width: 0;
}

.ck-content .todo-list li .todo-list {
	width: 100%;
	margin-left: 24px;
}

.ck-content .todo-list .todo-list__label {
	display: flex;
	align-items: center;
	flex-direction: row;
	gap: 8px;
	width: auto;
	flex: 0 0 auto;
	align-self: center;
	position: static !important;
	margin: 0 !important;
	padding: 0 !important;
}

.ck-content .todo-list .todo-list__label > input,
.ck-editor__editable.ck-content .todo-list .todo-list__label > span[contenteditable=false] > input {
	position: relative !important;
	left: 0;
	right: 0;
	margin: 0 8px 0 0;
}

.ck-content .todo-list .todo-list__label.todo-list__label_without-description input[type=checkbox] {
	position: relative !important;
}

.ck-content .todo-list .todo-list__label > span[contenteditable=false] {
	display: inline-flex;
	align-items: center;
}

.ck-content .todo-list .todo-list__label .todo-list__label__description {
	display: inline;
}

.ck-content .todo-list .todo-list__label > input::before,
.ck-editor__editable.ck-content .todo-list .todo-list__label > span[contenteditable=false] > input::before {
	border-radius: 4px;
	border-color: #7c3aed;
}

.ck-content .todo-list .todo-list__label > input[checked]::before,
.ck-editor__editable.ck-content .todo-list .todo-list__label > span[contenteditable=false] > input[checked]::before {
	background: #7c3aed;
	border-color: #7c3aed;
}

.ck-content .todo-list .todo-list__label:has(input[checked]) .todo-list__label__description,
.ck-content .todo-list .todo-list__label:has(span[contenteditable=false] > input[checked]) .todo-list__label__description {
	text-decoration: line-through;
	color: #777;
}

.ck-content .todo-list li:has(input[checked]) > div,
.ck-content .todo-list li:has(input[checked]) > p {
	text-decoration: line-through;
	color: #777;
}

/* === ИЗОБРАЖЕНИЯ === */
/* Figure должен подстраиваться под размер img */
.ck-content figure.image {
	max-width: 100%;
	position: relative;
	width: auto;
	height: auto;
	left: auto;
	top: auto;
	right: auto;
	bottom: auto;
	aspect-ratio: auto;
}

.ck-content figure.image img {
	max-width: 100%;
	min-width: 100%;
	height: auto;
}

.ck-content .image.image-style-side {
	max-width: 100%;
}
.ck .ck-widget__resizer {
	display: block;
	max-width: 100%;
	box-sizing: border-box;
}

.ck {
	/* Move resize handles inside the image box so they are not clipped */
	--ck-resizer-offset: 2px;
}

.ck .ck-widget__resizer__handle {
	width: 10px;
	height: 10px;
	background: #fff;
	border: 1px solid #3b82f6;
	border-radius: 3px;
	box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2);
}

.ck .ck-widget__resizer__handle:hover {
	background: #e6f0ff;
}

/* Show selection on the image, not the figure box */
.ck-content .ck-widget_selected figure.image {
	outline: none;
}

.ck-content .ck-widget_selected figure.image img {
	outline: 2px solid #3b82f6;
	outline-offset: 2px;
}

/* Подпись */
.ck-content figure.image > figcaption {
	display: block;
	color: #666;
	background: #f9f9f9;
	padding: 0.6em;
	font-size: 0.85em;
	text-align: center;
	margin-top: 0.5em;
}

/* Отключение встроенных тултипов CKEditor (глобально) */
.ck.ck-tooltip,
.ck.ck-balloon-panel.ck-tooltip,
.ck-body-wrapper .ck.ck-tooltip,
.ck-body-wrapper .ck.ck-balloon-panel.ck-tooltip {
	display: none !important;
	visibility: hidden !important;
	opacity: 0 !important;
	pointer-events: none !important;
}

/* Скрыть только настройки границ в окнах свойств таблицы/ячейки */
.ck.ck-table-properties-form .ck-form__row.ck-table-form__border-row,
.ck.ck-table-cell-properties-form .ck-form__row.ck-table-form__border-row {
	display: none !important;
}
</style>
